/* ==========================================================================
   PB Liquid Picker — controller

   TWO OBJECTS. A `Step` owns one basket: the counts on its cards, its cap, and
   what the bar should say while the visitor is in it. A `Picker` owns the
   section: the one sticky bar, the product sheet, the tier numbers, and which
   step is currently in view.

   That split is the whole point of the section holding every step. The bar
   exists once and the step in view borrows it — so scrolling from one step into
   the next never puts two bars on screen.

   THE BASKETS ARE VIRTUAL. Nothing here touches /cart. The shop runs cart sync,
   so a half-built bundle in the real cart would leak into every other surface.
   The picks live in localStorage under `pb_liquid_bundle_<bundle id>`, one key
   per step, as `[{ variantId, quantity, price, title }]` — the shape
   `pb_pick_mix_display_*` uses, so the step that adds the bundle to the cart
   can read either. The paid step also writes a `_latest` copy for that
   consumer; a free step does not, because two writers on one key is how they
   would start reading each other.

   Every change also goes out as a `pblp:change` event on `document`, carrying
   `{ bundleId, items, total, max }`. Listen for that rather than reading
   storage on a timer.

   CONVERGE. Four moments on the page go to the Converge pixel the theme loads
   in theme.liquid (`window.cvg`): the page being seen, a can going into the
   paid basket, a bottle being added on top of the free ones, and the tap on
   "Afrekenen". The metric names are built from the section's `track_name`
   setting ("WONDR Liquids Pre-sale" → "Viewed WONDR Liquids Pre-sale",
   "WONDR Liquids Pre-sale Can ATC", "… Bottle ATC", "… Checkout"), so a
   second page with this section can report under its own name — or under
   none, with the setting left blank. Every metric is mirrored as a
   `pblp:track` DOM event for tools that cannot hook the pixel. See
   `Picker.track`.

   A FREE STEP'S CAP IS NOT A SETTING. It is however many free cans the paid
   basket has unlocked, worked out from the tier numbers, and it follows that
   basket live through the same event. Below the first tier there is nothing to
   choose, so the step takes itself off the page.

   Markup and geometry: sections/pb-liquid-picker.liquid and
   assets/pb-liquid-picker.css.
   ========================================================================== */

(function () {
  'use strict';

  var STORE_PREFIX = 'pb_liquid_bundle_';

  /* The Converge metrics, with `{name}` for the section's `track_name`. The
     same four words the brief used: viewed the page, added a can, said yes to
     an extra bottle, tapped "Afrekenen". "ATC" because that is what the Pick &
     Mix metrics call a pick into a virtual basket, and the two should read as
     one family in Converge. */
  var TRACK_EVENTS = {
    viewed: 'Viewed {name}',
    can: '{name} Can ATC',
    bottle: '{name} Bottle ATC',
    checkout: '{name} Checkout',
  };

  /* localStorage throws rather than returning null in a locked-down Safari
     tab, so every touch of it is wrapped. A visitor who blocks storage still
     gets a working picker, it just does not survive a reload. */
  function readStore(key) {
    try {
      var raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  function writeStore(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      /* Full or blocked: the session keeps working in memory. */
    }
  }

  /* The theme editor rewrites the braces in a rich text setting as HTML
     entities the moment the merchant saves the page — `{count}` comes back as
     `&lcub;count&rcub;`. It renders as a brace, so nothing looks wrong in the
     editor, but the placeholder no longer matches and the visitor reads
     "Je verdiende {count} gratis flessen". Undoing it in the template only
     lasts until the next save, so it is undone here instead, on every value
     that goes through a placeholder. */
  function braces(template) {
    return String(template || '')
      .replace(/&lcub;|&#123;|&#x7b;/gi, '{')
      .replace(/&rcub;|&#125;|&#x7d;/gi, '}');
  }

  function fill(template, values) {
    return braces(template).replace(/\{(\w+)\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match;
    });
  }

  /* The same phrase, starting a sentence. What the ladder hands over is
     written in lower case because it usually lands mid-sentence ("voor extra
     fles + 2 blikjes"), and the one place it opens a line needs a capital. */
  function opener(value) {
    var text = String(value || '');
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* --------------------------------------------------------------------------
     A step — one basket, one grid
     -------------------------------------------------------------------------- */

  function Step(root, picker) {
    this.root = root;
    this.picker = picker;

    this.mode = root.getAttribute('data-pblp-mode') || 'tiers';
    this.bundleId = root.getAttribute('data-pblp-bundle') || 'latest';
    this.source = root.getAttribute('data-pblp-source') || '';
    this.addLabel = root.getAttribute('data-pblp-add-label') || '';
    /* The label above the step is a pattern with {n} in it, because which step
       this is depends on which steps are on the page — see Picker.paintLabels.
       `eyebrow` is what it currently reads; the section rendered the count of
       blocks into it, which is right until a step appears or disappears. */
    this.eyebrowPattern = root.getAttribute('data-pblp-eyebrow') || '';

    /* Meervoud: een stap draagt zijn titel twee keer, één keer op de foto en
       één keer in de flow, en welke van de twee te zien is hangt af van de
       breedte. Beide krijgen hetzelfde nummer. */
    this.eyebrowEls = Array.prototype.slice.call(
      root.querySelectorAll('[data-pblp-eyebrow-text]')
    );
    this.eyebrow =
      (this.eyebrowEls[0] && this.eyebrowEls[0].textContent.trim()) || this.eyebrowPattern;

    /* A bottles step sells as many bottles as the visitor wants, so it has no
       ceiling — only a floor, the ones the ladder already gave them. */
    this.max =
      this.mode === 'bottles' ? Infinity : parseInt(root.getAttribute('data-pblp-max'), 10) || 12;
    this.min = 0;

    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-pblp-card]'));
    this.notice = root.querySelector('[data-pblp-notice]');

    /* A free step on the page before anything is earned — see `sync`. The
       line under its title says how many cans that still takes. */
    this.locked = false;
    this.lockedNote = root.querySelector('[data-pblp-locked]');

    /* The paid step's line about the order minimum — see Picker.shortOfMin. */
    this.minNote = root.querySelector('[data-pblp-min]');

    this.seam = root.querySelector('[data-pblp-seam]');
    this.seamPill = root.querySelector('[data-pblp-seam-pill]');

    this.earnedLine = root.querySelector('[data-pblp-bottle-earned]');
    this.freeCard = root.querySelector('[data-pblp-bottle-free]');

    /* NOT `freeCount`: that is a method a summary step calls on itself, and an
       element under the same name on the instance shadows it — on a step
       without the element it lands there as null and the call throws. */
    this.freePill = root.querySelector('[data-pblp-bottle-free-count]');
    this.advice = root.querySelector('[data-pblp-bottle-advice]');

    this.summaryLines = root.querySelector('[data-pblp-summary-lines]');
    this.value = root.querySelector('[data-pblp-value]');
    this.valueRows = root.querySelector('[data-pblp-value-rows]');
    this.valueTotal = root.querySelector('[data-pblp-value-total]');

    /* variant id -> quantity. Strings as keys throughout: the ids come off
       data attributes, and a number key would miss a string lookup. */
    this.counts = {};

    this.restore();

    this.barReached = 0;
    this.barTotal = 0;
    this.flashTier = -1;
    this.flashTimer = null;
  }

  /* The second half of construction, run by the picker once EVERY step exists.
     A derived step reads the paid basket it derives from, and that basket is
     another step — so it cannot be read until that step is there. (It used to
     be read from localStorage instead, which needed no order but broke the
     rewards for anyone whose storage is blocked or full, and read back counts
     that `restore` had since filtered or capped.) */
  Step.prototype.settle = function () {
    /* Neither a free step's ceiling nor a bottles step's floor is a number of
       its own: both come out of what the paid basket has unlocked. Worked out
       before the first paint, and again whenever that basket changes. */
    if (this.derived()) this.sync(false);

    /* Where the ladder stood when the page loaded. Restoring a saved basket
       must not celebrate a tier the visitor reached yesterday, so the mark is
       set before the first paint and only a later crossing flashes. */
    this.barReached = this.picker.reachedCount(this.total());

    /* Same reason: a restored basket must not pop "+4" on arrival. */
    this.barTotal = this.total();
  };

  Step.prototype.total = function () {
    var self = this;
    return Object.keys(this.counts).reduce(function (sum, id) {
      return sum + self.counts[id];
    }, 0);
  };

  /* This step's own key and nothing else. There used to be a fall back to
     `_latest` for a bundle id that had been renamed, and with one basket on the
     page it was harmless; with one per step it would hand a step whatever
     another saved last, on a first visit, as its own picks. */
  Step.prototype.restore = function () {
    var saved = readStore(STORE_PREFIX + this.bundleId) || [];
    if (!Array.isArray(saved)) return;

    var known = {};
    this.cards.forEach(function (card) {
      known[card.getAttribute('data-pblp-variant')] = true;
    });

    var self = this;
    saved.forEach(function (item) {
      if (!item || !item.variantId) return;
      var id = String(item.variantId);
      /* Only restore variants this step still offers. A line-up edited in the
         theme editor would otherwise keep counting a can that is gone, and the
         cap would be short for no visible reason. */
      if (!known[id]) return;
      var quantity = parseInt(item.quantity, 10);
      if (quantity > 0) self.counts[id] = quantity;
    });

    this.clampToMax();
  };

  /* A cap lowered in the theme editor — or a free step's entitlement falling
     when cans go back out of the paid one — can leave a saved basket over the
     line. Trim from the end, so the visitor keeps what they picked first. */
  Step.prototype.clampToMax = function () {
    var ids = Object.keys(this.counts);
    var over = this.total() - this.max;
    for (var i = ids.length - 1; i >= 0 && over > 0; i -= 1) {
      var take = Math.min(this.counts[ids[i]], over);
      this.counts[ids[i]] -= take;
      over -= take;
      if (this.counts[ids[i]] <= 0) delete this.counts[ids[i]];
    }
  };

  Step.prototype.items = function () {
    var self = this;
    return this.cards
      .filter(function (card) {
        return self.counts[card.getAttribute('data-pblp-variant')] > 0;
      })
      .map(function (card) {
        var id = card.getAttribute('data-pblp-variant');
        return {
          variantId: id,
          quantity: self.counts[id],
          price: parseInt(card.getAttribute('data-pblp-price'), 10) || 0,
          title: card.getAttribute('data-pblp-title') || '',
        };
      });
  };

  Step.prototype.persist = function () {
    var items = this.items();
    writeStore(STORE_PREFIX + this.bundleId, items);

    /* The paid basket keeps the `_latest` alias it was given, for whatever adds
       the bundle to the cart. A free basket does not. */
    if (this.mode !== 'free') writeStore(STORE_PREFIX + 'latest', items);

    document.dispatchEvent(
      new CustomEvent('pblp:change', {
        detail: {
          bundleId: this.bundleId,
          items: items,
          total: this.total(),
          max: this.max,
        },
      })
    );
  };

  Step.prototype.set = function (variantId, quantity) {
    var id = String(variantId);
    var next = Math.max(0, quantity);

    /* Never let a single card push the basket past the cap: give it whatever
       room is left instead of refusing the tap outright. */
    var others = this.total() - (this.counts[id] || 0);
    next = Math.min(next, this.max - others);

    /* And never below the floor. A bottles step starts with the bottles the
       ladder gave away, and those are not something to hand back. */
    next = Math.max(next, this.min - others);

    if (next <= 0) {
      delete this.counts[id];
    } else {
      this.counts[id] = next;
    }

    /* Acting in a step is what makes it the step in view, whatever the scroll
       position says — a short step near the bottom of the page would otherwise
       hand the bar to its neighbour while the visitor is tapping in it. */
    this.picker.setActive(this);

    this.render();
    this.persist();
  };

  /* This step's own cards and its notice. The bar is the section's, and only
     the step in view gets to paint it. */
  Step.prototype.render = function () {
    var self = this;
    var total = this.total();
    var full = total >= this.max;
    var floored = total <= this.min;

    this.cards.forEach(function (card) {
      var id = card.getAttribute('data-pblp-variant');
      var quantity = self.counts[id] || 0;

      /* A bottles step's stepper counts the bottles the visitor is PAYING FOR.
         The basket holds the free ones as its floor — they are shown on their
         own card above — so the display is what is left on top of them. */
      var shown = self.mode === 'bottles' ? quantity - self.min : quantity;

      self.picker.paintControl(
        card.querySelector('[data-pblp-control]'),
        quantity,
        full,
        floored,
        shown
      );
      card.classList.toggle('is-picked', quantity > 0);

      /* A full free step greys the cans that did not make it. The disabled
         plus said so before, but one card at a time and quietly; with the
         cards stepping back and the line under the grid (the notice) the
         visitor sees at a glance that this step is done. Paid steps keep
         their look — at twelve cans a greyed card would read as sold out. */
      card.classList.toggle('is-dimmed', self.mode === 'free' && full && quantity === 0);
    });

    /* A locked step is "full" at nought — its cap is nought — so the cards
       grey and the buttons go off by the same rule; only the words differ:
       not "all free cans chosen" but how many more cans that takes. */
    if (this.notice) this.notice.classList.toggle('is-visible', full && !this.locked);

    /* "Kies minstens 3 blikjes om te genieten van de pre-sale korting", under
       the paid step's grid until the basket gets there — where the next step
       would have been, since every step below waits for the minimum. */
    if (this.minNote) {
      var short = this.picker.shortOfMin();
      this.minNote.hidden = short <= 0;
      if (short > 0) {
        var ask = this.picker.minText(this.picker.copy.minStep, short);
        if (this.minNote.textContent !== ask) this.minNote.textContent = ask;
      }
    }

    if (this.lockedNote) {
      this.lockedNote.hidden = !this.locked;
      if (this.locked) {
        var promise = this.picker.lockedText(this);
        if (this.lockedNote.textContent !== promise) this.lockedNote.textContent = promise;
      }
    }
    if (this.mode === 'bottles') this.paintBottles();
    if (this.mode === 'summary') this.paintSummary();

    /* The sheet stands over whichever card opened it, so it follows that
       card's step rather than the step in view. */
    if (this.picker.openCard && this.picker.stepOf(this.picker.openCard) === this) {
      this.picker.paintSheetControl();
    }

    if (this.picker.owner() === this) this.paintBar();
  };

  /* --------------------------------------------------------------------------
     A free step

     Its cap is the number of free cans the paid basket has unlocked, so it is
     read from that basket rather than from a setting of its own — and it
     follows it live, because every step sits on the same page and the paid one
     announces every change.
     -------------------------------------------------------------------------- */

  /* Both kinds of derived step read the same basket; they differ only in which
     of the tier's two rewards they are about. */
  Step.prototype.derived = function () {
    return this.mode === 'free' || this.mode === 'bottles';
  };

  /* Whether this step is on the page at all. A derived step hides itself when
     the paid basket has unlocked nothing for it, and a hidden step's basket
     must not count: it still holds what the visitor picked when the ladder did
     reach that far, and localStorage keeps it across visits. Without this the
     summary of an empty page reads back a bottle from last week — and, worse,
     the hand-off would put it in the checkout. */
  Step.prototype.onPage = function () {
    return !this.root.hidden && !this.locked;
  };

  /* On screen, counting or not: a locked free step is shown — it gets a step
     number, it can own the bar — but nothing in it counts. */
  Step.prototype.shown = function () {
    return !this.root.hidden;
  };

  /* How many paid cans are in the basket this step derives from. THE STEP ON
     THE PAGE IS THE SOURCE, not localStorage: storage is where a basket is
     kept between visits, and the step's own count is what it holds now. Read
     from storage this went to 0 the moment storage was blocked or full — and
     with it the free cans, the bottles and the percentage, while the paid
     step carried on as if nothing was wrong — and it could read back a count
     for a can that `restore` had dropped or capped. Storage stays as the
     fallback for a source step that is not on the page at all. */
  Step.prototype.bought = function () {
    var source = this.picker.stepFor(this.source);
    if (source && source !== this) return source.total();

    var saved = readStore(STORE_PREFIX + this.source);
    if (!Array.isArray(saved)) return 0;

    var total = 0;
    saved.forEach(function (item) {
      var quantity = parseInt(item && item.quantity, 10);
      if (quantity > 0) total += quantity;
    });
    return total;
  };

  Step.prototype.entitlement = function () {
    if (!this.source || !this.picker.tiers.length) return 0;

    var reached = this.picker.reachedCount(this.bought());
    if (reached <= 0) return 0;

    var tier = this.picker.tiers[reached - 1];
    return (this.mode === 'bottles' ? tier.bottles : tier.freeCans) || 0;
  };

  /* Takes `amount` bottles back out of the basket, from the first card first —
     the card the ladder puts its bottles on — so what goes is what the ladder
     gave and what stays is what the visitor added on top. */
  Step.prototype.trim = function (amount) {
    var self = this;
    this.cards.forEach(function (card) {
      if (amount <= 0) return;
      var id = card.getAttribute('data-pblp-variant');
      var have = self.counts[id] || 0;
      var take = Math.min(have, amount);
      if (take <= 0) return;
      if (have > take) self.counts[id] = have - take;
      else delete self.counts[id];
      amount -= take;
    });
  };

  /* The entitlement can fall as well as rise — taking cans out of the paid step
     takes the reward away with them — so this brings the basket back in line
     and takes the step off the page when there is nothing left to say. */
  Step.prototype.sync = function (live) {
    var before = this.total();
    var earned = this.entitlement();

    if (this.mode === 'bottles') {
      /* The bottles the ladder gave are in the package already, so they are
         the floor rather than the count: raise the basket to meet it, and let
         the visitor add as many more as they like on top. */
      var previous = this.earned || 0;
      this.min = earned;
      this.earned = earned;
      if (this.total() < earned && this.cards.length) {
        var id = this.cards[0].getAttribute('data-pblp-variant');
        this.counts[id] = (this.counts[id] || 0) + (earned - this.total());

        /* The ladder handing over a bottle is not a tap to celebrate, so the
           badge's mark moves with it and the next paint sees no increase. */
        this.barTotal = this.total();
      } else if (earned < previous) {
        /* THE LADDER TAKING BOTTLES BACK. Lowering the floor alone left the
           bottles it had put in the basket standing, and everything above the
           new floor reads as paid — so a visitor who tried the top tier and
           then chose 3 cans was sold the 2 bottles the page had handed them
           for free. What the ladder gave goes back out with the tier; what the
           visitor added on top of it stays. */
        this.trim(Math.min(previous - earned, this.total()));
        this.barTotal = this.total();
      }

      /* Bottles are for the cans in the basket, so with an empty basket there
         is nothing here to size — and under the order minimum nothing but the
         paid step is on the page at all. */
      this.root.hidden = this.bought() <= 0 || this.picker.shortOfMin() > 0;
    } else {
      this.max = earned;
      this.clampToMax();

      /* Nothing earned yet, nothing to choose. Hidden rather than empty: a step
         whose every card is dead, under a bar pointing back up the page, is
         worse than no step.

         EXCEPT BY CHOICE (Ruben and Brent, 8 and 9 Sep): the free step stays
         on the page, LOCKED — cards greyed, buttons off, and a line under the
         title saying how many more cans it takes — so the visitor sees what
         there is to earn. First desktop only (where the bar is not on the
         page), since 9 Sep on the phone too. The merchant can turn that off
         (`lockFree`); a ladder without free cans never locks, there would be
         nothing to promise. A locked step is on screen but not "on the page"
         for anything that counts — see `onPage` — and it never owns the bar,
         which keeps telling the paid step's story — see `refreshActive`. */
      var lockable = this.mode === 'free' && this.picker.lockFree && !!this.picker.firstFreeTier();
      /* Under the order minimum the page is the paid step alone (Brent, 8
         Sep): no locked step either, the line under step 1 does the telling. */
      var below = this.picker.shortOfMin() > 0;
      this.locked = !below && earned <= 0 && lockable;
      this.root.hidden = below || (earned <= 0 && !this.locked);
      this.root.classList.toggle('is-locked', this.locked);
    }

    if (!live) return;
    this.picker.refreshActive();
    this.render();
    if (this.total() !== before) this.persist();
  };

  /* --------------------------------------------------------------------------
     What the bar says in this step

     The tiers arrive as numbers — how many cans a tier asks for, and how many
     free bottles and cans it is worth IN TOTAL at that point. Two readings come
     out of that, and the bar needs both:

       the step   what this tier adds on top of the one below it. This is what
                  the bar promises ahead: at 4 cans of 3/6/9/12 the next tier
                  is worth one more bottle and two more cans, so it says "voor
                  1 fles + 2 blikjes" — not the two bottles the tier holds in
                  total, one of which is already in.
       the total  everything up to and including the highest tier reached. This
                  is the "al ontgrendeld" line.

     Deriving both from one set of numbers is the point. The wireframe wrote
     them out by hand and its middle states had drifted a bottle apart from the
     ladder; numbers cannot drift.
     -------------------------------------------------------------------------- */

  /* Everything up to and including a tier: "2 flessen · 4 blikjes · 10% korting". */
  Step.prototype.totalPhrase = function (index) {
    var picker = this.picker;
    var tier = picker.tiers[index];
    var copy = picker.copy;
    var parts = [];

    if (tier.bottles > 0) {
      parts.push(picker.countNoun(tier.bottles, copy.bottleOne, copy.bottleMany));
    }
    if (tier.freeCans > 0) {
      parts.push(picker.countNoun(tier.freeCans, copy.canOne, copy.canMany));
    }
    /* A perk that carries on once it is unlocked — a discount, say — reads
       naturally when it is entered on every tier from there up, and that is how
       it gets entered. Listing it once is the only sane reading either way. */
    for (var i = 0; i <= index; i += 1) {
      var perk = picker.tiers[i].perk;
      if (perk && parts.indexOf(perk) === -1) parts.push(perk);
    }

    return parts.join(picker.glue());
  };

  /* The extra advantages the ladder has handed over, de-duplicated — a discount
     entered on every tier from where it starts is still one discount. */
  Step.prototype.perk = function () {
    var picker = this.picker;
    var reached = picker.reachedCount(this.bought());
    var parts = [];
    for (var i = 0; i < reached; i += 1) {
      var perk = picker.tiers[i].perk;
      if (perk && parts.indexOf(perk) === -1) parts.push(perk);
    }
    return parts.join(picker.glue());
  };

  Step.prototype.paintBar = function () {
    var picker = this.picker;
    if (!picker.bar) return;

    var total = this.total();

    if (total > this.barTotal) picker.flashBadge(total - this.barTotal);
    this.barTotal = total;

    if (this.mode === 'free') {
      this.paintFreeBar();
      return;
    }
    if (this.mode === 'bottles') {
      this.paintBottlesBar();
      return;
    }
    if (this.mode === 'summary') {
      this.paintSummaryBar();
      return;
    }

    var copy = picker.copy;
    var reached = picker.reachedCount(total);

    /* A tier just fell. Hold the moment, then let the bar look ahead again. */
    if (reached > this.barReached && picker.flashMs > 0) {
      this.flashTier = reached - 1;
      window.clearTimeout(this.flashTimer);
      var self = this;
      this.flashTimer = window.setTimeout(function () {
        self.flashTier = -1;
        if (picker.owner() === self) self.paintBar();
      }, picker.flashMs);
    }
    /* Stepping back below a tier cancels a celebration still on screen. */
    if (reached < this.barReached) {
      this.flashTier = -1;
      window.clearTimeout(this.flashTimer);
    }
    this.barReached = reached;

    picker.buildTierRow(this.max);

    var state;
    var title;

    /* The second half of the line is what the next tier costs. Reaching a tier
       and finishing the ladder are the two moments there is nothing to ask for,
       and then it is empty — which is also how the artboard draws those two. */
    var reward = '';

    if (reached >= picker.tiers.length) {
      /* The whole ladder is in. This is the celebration, so it outranks a
         flash for the last tier — there is nothing left to look ahead to. */
      state = 'complete';
      title = copy.completeTitle;
    } else if (this.flashTier >= 0) {
      /* The one moment on the page where the gift can be named, so it is: the
         line under the bar already carries the running total, which makes this
         the place for what just came in. `step()` is the difference with the
         tier below, so the phrase is "1 extra fles + 2 blikjes" rather than the
         two flessen the package now holds. */
      state = 'unlocked';
      var gift = picker.rewardPhrase(picker.step(this.flashTier));
      title = fill(copy.tierTitle, {
        tier: this.flashTier + 1,
        reward: gift,
        Reward: opener(gift),
      });
    } else {
      var missing = picker.tiers[reached].cans - total;
      state = 'next';
      title = fill(copy.remaining, {
        count: missing,
        unit: missing === 1 ? copy.canOne : copy.canMany,
      });
      reward = fill(copy.reward, { reward: picker.rewardPhrase(picker.step(reached)) });
    }

    picker.paint({
      state: state,
      title: title,
      reward: reward,
      on: total,
      max: this.max,
      label: copy.unlockedLabel,
      value: reached === 0 ? copy.unlockedEmpty : this.totalPhrase(reached - 1),
      empty: reached === 0,
    });
  };

  /* --------------------------------------------------------------------------
     A bottles step (node 403:429)

     How many refillable bottles go in the package, told in two cards. The
     first is what the ladder gave: a sentence above it, a count on a green
     pill, and nothing to act on — both go while nothing is unlocked. Between
     the cards, the one number worth recommending: a bottle per kind of can, so
     no two scents have to share one. The second card is where the visitor pays
     for more, and it counts only those.

     THE BASKET STILL HOLDS BOTH. The free bottles are its floor; splitting
     them out is a matter of what the stepper displays (Step.render), not of
     what goes to the checkout.
     -------------------------------------------------------------------------- */

  Step.prototype.paintBottles = function () {
    var picker = this.picker;
    var copy = picker.copy;
    var earned = this.earned || 0;
    var advised = picker.varieties();

    /* What the ladder gave. Rich text, so the merchant decides which half of
       the sentence is bold. The line and the card it introduces go together:
       until a tier is reached there is nothing to say and nothing to show. */
    if (this.earnedLine) {
      var perk = earned > 0 ? this.perk() : '';
      this.earnedLine.innerHTML =
        earned > 0
          ? picker.fillRich(perk ? copy.bottlesEarnedPerk : copy.bottlesEarned, {
              count: earned,
              unit: picker.noun(earned, copy.bottleOne, copy.bottleMany),
              perk: perk,
            })
          : '';
      this.earnedLine.hidden = earned <= 0;
    }

    if (this.freeCard) this.freeCard.hidden = earned <= 0;

    /* The count on the green pill is what the ladder handed over, which is
       also this step's floor — the bottles below it are the paid ones. */
    if (this.freePill) {
      this.freePill.textContent = fill(copy.bottlesIncluded, {
        count: earned,
        unit: picker.noun(earned, copy.bottleOne, copy.bottleMany),
      });
    }

    /* One bottle per kind of can in the basket, so no two scents share one. */
    if (this.advice) {
      this.advice.innerHTML =
        advised > 0
          ? picker.fillRich(copy.bottlesAdvice, {
              count: advised,
              unit: picker.noun(advised, copy.bottleOne, copy.bottleMany),
            })
          : '';
      this.advice.hidden = advised <= 0;
    }
  };

  /* Two halves of one line: how many bottles are in, and how that number is
     made up. */
  Step.prototype.paintBottlesBar = function () {
    var picker = this.picker;
    var copy = picker.copy;
    var total = this.total();
    var earned = Math.min(total, this.earned || 0);
    var extra = total - earned;

    picker.buildBottleRow(total, earned);

    picker.paint({
      state: 'bottles',
      title: fill(copy.bottlesBarTitle, {
        count: total,
        unit: picker.noun(total, copy.bottleOne, copy.bottleMany),
      }),
      reward: '',
      on: total,
      max: total,
      label: copy.bottlesBarLabel,
      value: extra > 0
        ? fill(copy.bottlesBarValue, { free: earned, extra: extra })
        : fill(copy.bottlesBarValueFree, { free: earned }),
      empty: total <= 0,
      progress: fill(copy.bottlesProgress, {
        count: total,
        unit: picker.noun(total, copy.bottleOne, copy.bottleMany),
      }),
    });
  };

  /* --------------------------------------------------------------------------
     A summary step

     The last one: everything the steps above it hold, read back, priced, and
     added up. It owns no basket of its own — its counts come from the others —
     and every amount is worked out from the products' own prices and the
     tier's discount, so there is no figure here for anyone to keep in step
     with anything else.
     -------------------------------------------------------------------------- */

  /* One row per variant, merged across the steps that carry it: the same body
     wash bought in step one and taken free in step two is one line. */
  Step.prototype.summaryGroups = function () {
    var picker = this.picker;
    var groups = [
      { kind: 'cans', label: picker.copy.sumCansLabel, rows: [], total: 0 },
      { kind: 'bottles', label: picker.copy.sumBottlesLabel, rows: [], total: 0 },
    ];
    var index = {};

    picker.steps.forEach(function (step) {
      if (step.mode === 'summary' || !step.onPage()) return;

      var kind = step.mode === 'bottles' ? 'bottles' : 'cans';
      var group = kind === 'bottles' ? groups[1] : groups[0];

      /* In a bottles step the ladder's share is free and the rest was added;
         everywhere else the step itself says which it is. */
      var freeSoFar = step.mode === 'bottles' ? step.earned || 0 : 0;

      step.cards.forEach(function (card) {
        var id = card.getAttribute('data-pblp-variant');
        var quantity = step.counts[id] || 0;
        if (quantity <= 0) return;

        var free;
        if (step.mode === 'free') free = quantity;
        else if (step.mode === 'bottles') {
          free = Math.min(quantity, freeSoFar);
          freeSoFar -= free;
        } else free = 0;

        var key = kind + ':' + id;
        var row = index[key];
        if (!row) {
          /* "Blossom Bliss · Body Wash": on its own card the scent is an
             eyebrow above the name, but in a list of ten rows the name alone
             does not say which body wash this is. */
          var name = card.getAttribute('data-pblp-title') || '';
          var scent = card.getAttribute('data-pblp-eyebrow') || '';

          row = {
            id: id,
            title: scent ? scent + picker.glue() + name : name,
            price: parseInt(card.getAttribute('data-pblp-price'), 10) || 0,
            /* A card that shows no photo of its own (the bottle) carries one
               for this line in a hidden `[data-pblp-thumb]`; a can card's
               first image is its photo. */
            image: card.querySelector('[data-pblp-thumb] img') || card.querySelector('img'),
            paid: 0,
            free: 0,
          };
          index[key] = row;
          group.rows.push(row);
        }
        row.paid += quantity - free;
        row.free += free;
        group.total += quantity;
      });
    });

    return groups.filter(function (group) {
      return group.rows.length > 0;
    });
  };

  /* What the bundle is worth, what of it is a gift, and what is left to pay.
     The discount is the reached tier's, and it applies to what is payable —
     a percentage off something already free is not a saving. */
  Step.prototype.summaryMoney = function (groups) {
    var picker = this.picker;
    var money = { list: 0, freeCans: 0, freeBottles: 0, percent: 0, discount: 0 };

    groups.forEach(function (group) {
      group.rows.forEach(function (row) {
        money.list += row.price * (row.paid + row.free);
        if (group.kind === 'bottles') money.freeBottles += row.price * row.free;
        else money.freeCans += row.price * row.free;
      });
    });

    var reached = picker.reachedCount(this.bought());
    money.percent = reached > 0 ? picker.tiers[reached - 1].discount || 0 : 0;

    var payable = money.list - money.freeCans - money.freeBottles;
    /* Down to whole cents, not to the nearest one. Shopify truncates a
       discount, so rounding up here would promise a cent the checkout does not
       give — and the cart transform in planet-b-online-discount truncates the
       same way, so page and checkout land on the same number. */
    money.discount = Math.floor((payable * money.percent) / 100);
    money.saved = money.freeCans + money.freeBottles + money.discount;
    money.total = money.list - money.saved;
    return money;
  };

  Step.prototype.paintSummary = function () {
    var picker = this.picker;
    var copy = picker.copy;
    var groups = this.summaryGroups();
    this.money = this.summaryMoney(groups);

    /* Nothing picked yet, nothing to summarise. */
    /* Nothing to sum, or the basket is short of the order minimum: then the
       overview — and with it the way out — waits. */
    this.root.hidden = !groups.length || this.picker.shortOfMin() > 0;
    if (this.root.hidden) return;

    if (this.summaryLines) {
      var list = document.createDocumentFragment();
      groups.forEach(function (group) {
        var head = document.createElement('p');
        head.className = 'pb-liquid-picker__summary-group';
        head.textContent = group.label + ' · ' + group.total;
        list.appendChild(head);

        group.rows.forEach(function (row) {
          list.appendChild(picker.summaryRow(row, group.kind));
        });
      });
      this.summaryLines.replaceChildren(list);
    }

    if (this.valueRows) {
      var rows = document.createDocumentFragment();
      var money = this.money;

      if (money.freeCans > 0) {
        rows.appendChild(
          picker.valueRow(
            fill(copy.sumValueCans, {
              count: this.freeCount(groups, 'cans'),
              unit: picker.noun(this.freeCount(groups, 'cans'), copy.canOne, copy.canMany),
            }),
            money.freeCans,
            'gift'
          )
        );
      }
      if (money.freeBottles > 0) {
        rows.appendChild(
          picker.valueRow(
            fill(copy.sumValueBottles, {
              count: this.freeCount(groups, 'bottles'),
              unit: picker.noun(
                this.freeCount(groups, 'bottles'),
                copy.bottleOne,
                copy.bottleMany
              ),
            }),
            money.freeBottles,
            'gift'
          )
        );
      }
      if (money.discount > 0) {
        rows.appendChild(
          picker.valueRow(
            fill(copy.sumValueDiscount, { percent: money.percent }),
            money.discount,
            'discount'
          )
        );
      }
      this.valueRows.replaceChildren(rows);
    }

    if (this.value) this.value.hidden = this.money.saved <= 0;
    if (this.valueTotal) this.valueTotal.textContent = picker.money(this.money.saved);
  };

  Step.prototype.freeCount = function (groups, kind) {
    var total = 0;
    groups.forEach(function (group) {
      if (group.kind !== kind) return;
      group.rows.forEach(function (row) {
        total += row.free;
      });
    });
    return total;
  };

  /* The bar under a summary step is a price and a way out rather than a score. */
  Step.prototype.paintSummaryBar = function () {
    var picker = this.picker;
    var money = this.money || { list: 0, total: 0, saved: 0 };

    picker.paint({
      state: 'summary',
      title: '',
      reward: '',
      on: 0,
      max: 0,
      label: '',
      value: '',
      empty: true,
      progress: '',
    });

    if (picker.barSubtotal) picker.barSubtotal.textContent = picker.money(money.list);
    if (picker.barTotal) picker.barTotal.textContent = picker.money(money.total);
    if (picker.barSaved) picker.barSaved.textContent = picker.money(money.saved);
    if (picker.barSavedRow) picker.barSavedRow.hidden = money.saved <= 0;

    picker.gateCheckout();
  };

  /* One line per state: how many free cans are left to choose, or that they are
     all chosen. There is no third state on screen — with nothing earned the
     step is not on the page. */
  Step.prototype.paintFreeBar = function () {
    var picker = this.picker;
    var copy = picker.copy;
    var chosen = this.total();
    var allowed = this.max;

    picker.buildFreeRow(allowed);

    var state = chosen >= allowed ? 'complete' : 'next';
    var title = copy.freeDone;
    var suffix = '';

    if (state === 'next') {
      var missing = allowed - chosen;
      title = fill(copy.freeRemaining, {
        count: missing,
        unit: missing === 1 ? copy.canOne : copy.canMany,
      });
      suffix = copy.freeSuffix;
    }

    picker.paint({
      state: state,
      title: title,
      reward: suffix,
      on: chosen,
      max: allowed,
      label: copy.freeLabel,
      value: fill(copy.freeValue, { count: chosen, max: allowed }),
      empty: false,
    });
  };

  /* --------------------------------------------------------------------------
     The section
     -------------------------------------------------------------------------- */

  function Picker(root) {
    this.root = root;

    this.sheet = root.querySelector('[data-pblp-sheet]');
    this.backdrop = root.querySelector('[data-pblp-backdrop]');
    this.sheetSlot = root.querySelector('[data-pblp-sheet-slot]');
    this.sheetControl = root.querySelector('[data-pblp-sheet-control]');
    this.sheetAdd = this.sheetControl && this.sheetControl.querySelector('[data-pblp-add]');
    this.dots = root.querySelector('[data-pblp-dots]');
    this.openCard = null;
    this.lastFocus = null;

    /* The one close in flight (its timer, its listener and its number), and
       the frames an open is still waiting on — see `close`. */
    this.closeTimer = 0;
    this.closeListener = null;
    this.closeToken = 0;
    this.openFrame = 0;

    /* What the open sheet took out of reach — see `open`. */
    this.inerted = [];

    /* Whether the section is anywhere near the viewport — see `bind`. */
    this.inView = true;
    this.viewWatcher = null;

    /* First idle pass that warms sheet shots — see `warmIdle`. */
    this.sheetWarmQueued = false;

    this.readConfig();
    this.readBar();

    /* Whether the desktop layer has this picker in its column. The layer
       stamps the attribute before the steps settle and calls `setDesk` when
       the width changes; a free step reads it to decide whether to lock
       rather than hide. */
    this.desk = root.hasAttribute('data-pblp-desk');

    var self = this;
    this.steps = Array.prototype.slice
      .call(root.querySelectorAll('[data-pblp-step]'))
      .map(function (element) {
        return new Step(element, self);
      });
    /* In page order, so a paid step is settled before the steps that read it. */
    this.steps.forEach(function (step) {
      step.settle();
    });

    this.active = null;
    this.refreshActive();

    this.bind();
    /* Every step paints its own cards; the one in view also paints the bar.
       A summary step decides while painting whether it is on the
       page at all, so which step is in view is settled after that. */
    this.steps.forEach(function (step) {
      step.render();
    });
    this.refreshActive();

    /* The page is on screen with the picker in it. Last, so what the visitor
       brought back from a previous visit is already counted. */
    this.trackView();

    /* `?can=shampoo` in the address: land on the cans with that sheet open.
       Armed here, runs at `load` — see `deepLink`. */
    this.deepLink();
  }

  Picker.prototype.readConfig = function () {
    this.tiers = [];
    this.copy = {};
    this.flashMs = 0;
    this.badgeMs = 0;
    this.trackName = '';

    var config = this.root.querySelector('[data-pblp-config]');
    var parsed = null;
    try {
      parsed = config ? JSON.parse(config.textContent) : null;
    } catch (error) {
      parsed = null;
    }
    if (!parsed) return;

    /* Sorted here rather than in Liquid, which cannot sort on a nested block
       setting. The progress row is grouped in block order, so a merchant who
       enters the tiers out of order still gets the right wording — the groups
       are what would look odd, and the schema says to keep them in order. */
    this.tiers = (parsed.tiers || []).slice().sort(function (a, b) {
      return a.cans - b.cans;
    });
    this.copy = parsed.copy || {};
    this.flashMs = Math.max(0, parseInt(parsed.flashMs, 10) || 0);
    this.badgeMs = Math.max(0, parseInt(parsed.badgeMs, 10) || 0);
    this.trackName = String(parsed.trackName || '').trim();
    this.lockFree = !!parsed.lockFree;
    this.minCans = Math.max(0, parseInt(parsed.minCans, 10) || 0);
  };

  /* --------------------------------------------------------------------------
     The order minimum

     A pre-sale order is at least `minCans` paid cans (a setting; 0 turns it
     off). Under that the paid step is the whole page: the free step, the
     bottles and the overview stay hidden (each step's `sync` / `paintSummary`
     asks `shortOfMin`), and a line under the paid grid says what it takes —
     "Kies minstens 3 blikjes om te genieten van de pre-sale korting". The
     checkout button is off as well and `addToCart` refuses, for a click that
     reaches them anyway. The checkout side does not enforce it; this is a
     rule of the page.
     -------------------------------------------------------------------------- */

  /* Paid cans in the basket: the tiers steps that are on the page. */
  Picker.prototype.paidTotal = function () {
    return this.steps.reduce(function (sum, step) {
      return step.mode === 'tiers' && step.onPage() ? sum + step.total() : sum;
    }, 0);
  };

  /* How many paid cans short of the minimum; 0 when met or when there is none. */
  Picker.prototype.shortOfMin = function () {
    if (!this.minCans) return 0;
    return Math.max(0, this.minCans - this.paidTotal());
  };

  Picker.prototype.minText = function (template, short) {
    return fill(template || '', {
      min: this.minCans,
      count: short,
      unit: short === 1 ? this.copy.canOne : this.copy.canMany,
    });
  };

  /* The button follows the minimum whenever it is not busy with a hand-off.
     Under the minimum the overview is off the page anyway (Step.paintSummary),
     so this is the belt to that pair of braces. */
  Picker.prototype.gateCheckout = function () {
    if (this.checkout && !this.sending) this.checkout.disabled = this.shortOfMin() > 0;
  };

  /* --------------------------------------------------------------------------
     The locked free step (desktop)
     -------------------------------------------------------------------------- */

  /* Told by the desktop layer whether the picker stands in its column. A free
     step that hides itself on a phone stays on the page there, locked, until
     the paid basket earns it — so a change of width re-decides that. */
  Picker.prototype.setDesk = function (on) {
    on = !!on;
    if (this.desk === on) return;
    this.desk = on;
    this.steps.forEach(function (step) {
      if (step.mode === 'free') step.sync(true);
    });
  };

  /* The lowest tier that gives free cans, or null when the ladder never does. */
  Picker.prototype.firstFreeTier = function () {
    for (var i = 0; i < this.tiers.length; i += 1) {
      if ((this.tiers[i].freeCans || 0) > 0) return this.tiers[i];
    }
    return null;
  };

  /* "Koop nog minstens 3 blikjes om gratis blikjes te ontvangen": how many
     paid cans the visitor is short of the first tier with free cans. */
  Picker.prototype.lockedText = function (step) {
    var tier = this.firstFreeTier();
    if (!tier) return '';
    var missing = Math.max(0, tier.cans - step.bought());
    return fill(this.copy.lockedNotice || '', {
      count: missing,
      unit: missing === 1 ? this.copy.canOne : this.copy.canMany,
    });
  };

  /* --------------------------------------------------------------------------
     Converge

     One method sends, three methods say what. Nothing here reaches the pixel
     while the section has no `track_name` or the page is open in the theme
     editor — a merchant clicking through the customizer is not a visitor.
     -------------------------------------------------------------------------- */

  Picker.prototype.track = function (key, properties) {
    if (!this.trackName || !TRACK_EVENTS[key]) return;
    if (window.Shopify && window.Shopify.designMode) return;

    var eventName = fill(TRACK_EVENTS[key], { name: this.trackName });
    var props = Object.assign(
      {
        track_name: this.trackName,
        page_path: window.location.pathname,
      },
      properties || {}
    );

    try {
      /* The pixel loads async; theme.liquid puts a queue in its place until
         it does, and this is the same queue in case this ran first. */
      if (!window.cvg) {
        var queue = function () {
          queue.process ? queue.process.apply(queue, arguments) : queue.queue.push(arguments);
        };
        queue.queue = [];
        window.cvg = queue;
      }
      window.cvg({ method: 'track', eventName: eventName, properties: props });
    } catch (_error) {
      /* A pixel that throws must not take the picker down with it. */
    }

    try {
      document.dispatchEvent(
        new CustomEvent('pblp:track', { detail: { eventName: eventName, properties: props } })
      );
    } catch (_error) {
      /* Nothing to do without CustomEvent. */
    }
  };

  Picker.prototype.currency = function () {
    var shopify = window.Shopify;
    return (shopify && shopify.currency && shopify.currency.active) || 'EUR';
  };

  /* The paid step: the one basket every reward is worked out from. */
  Picker.prototype.paidStep = function () {
    for (var i = 0; i < this.steps.length; i += 1) {
      if (this.steps[i].mode === 'tiers') return this.steps[i];
    }
    return null;
  };

  Picker.prototype.trackView = function () {
    var paid = this.paidStep();
    var cans = paid ? paid.total() : 0;
    this.track('viewed', {
      bundle_id: paid ? paid.bundleId : '',
      cans_in_basket: cans,
      tier_reached: this.reachedCount(cans),
    });
  };

  /* A can into the paid basket, or a bottle on top of the free ones. Called
     after the tap has landed, and only when it added something: a tap on a
     full basket changes nothing and is not an add. `via` says which face of
     the control was hit — the labelled button ("Toevoegen", "Ja, graag") or
     the plus of the stepper — and `from_sheet` whether it was the product
     sheet's copy of that control. */
  Picker.prototype.trackAdd = function (step, card, button) {
    var key;
    if (step.mode === 'tiers') key = 'can';
    else if (step.mode === 'bottles') key = 'bottle';
    else return;

    var id = card.getAttribute('data-pblp-variant');
    var props = {
      via: button.hasAttribute('data-pblp-add') ? 'add' : 'increase',
      from_sheet: !button.closest('[data-pblp-card]'),
      variant_id: id,
      name: card.getAttribute('data-pblp-title') || '',
      scent: card.getAttribute('data-pblp-eyebrow') || '',
      price: (parseInt(card.getAttribute('data-pblp-price'), 10) || 0) / 100,
      currency: this.currency(),
      quantity: step.counts[id] || 0,
    };

    if (key === 'can') {
      props.cans_total = step.total();
      props.tier_reached = this.reachedCount(step.total());
    } else {
      /* The floor is what the ladder gave; everything above it is paid. */
      props.bottles_free = step.min;
      props.bottles_paid = step.total() - step.min;
      props.bottles_total = step.total();
    }

    this.track(key, props);
  };

  /* WHICH cans are in the bundle, as kinds rather than counts: "BLOSSOM BLISS ·
     Body Wash" once, whether there are two of it or six. Three lists — every
     kind, the kinds paid for, the kinds taken free — so Converge can answer
     "who buys the shampoo" and "what do people pick as their gift" without
     the quantities getting in the way (those are in cans_paid / cans_free).
     Same label the overview uses, so the two read the same. */
  Picker.prototype.canTypes = function () {
    var picker = this;
    var seen = { all: {}, paid: {}, free: {} };
    var out = { all: [], paid: [], free: [], ids: [] };

    var note = function (bucket, label) {
      if (seen[bucket][label]) return;
      seen[bucket][label] = true;
      out[bucket].push(label);
    };

    this.steps.forEach(function (step) {
      if (step.mode !== 'tiers' && step.mode !== 'free') return;
      if (!step.onPage()) return;

      step.cards.forEach(function (card) {
        var id = card.getAttribute('data-pblp-variant');
        if (!(step.counts[id] > 0)) return;

        var name = card.getAttribute('data-pblp-title') || '';
        var scent = card.getAttribute('data-pblp-eyebrow') || '';
        var label = scent ? scent + picker.glue() + name : name;

        note('all', label);
        note(step.mode === 'free' ? 'free' : 'paid', label);
        if (out.ids.indexOf(id) === -1) out.ids.push(id);
      });
    });

    return out;
  };

  /* The tap on "Afrekenen", with the bundle as it leaves: the counts from the
     same lines that go to the checkout, the money from the overview, which
     cans are in it, and THE CHECKOUT URL. That last one is why this is sent
     after the hand-off and not on the tap: the URL exists only once the shop
     has made the cart, and it is what lets a Klaviyo flow send the person
     straight back into a checkout with their bundle already in it. It is the
     /checkouts/cn/<token> form for that reason — a link that survives a
     click tracker and does not route through the cart — and not the cart
     permalink the page itself navigates to. A
     hand-off that failed still counts as a tap — same event, empty URL,
     `checkout_status` says so — so the flow filters on the URL being there
     and the funnel keeps every tap. */
  Picker.prototype.trackCheckout = function (lines, checkoutUrl, errorCode) {
    var counts = { cans_paid: 0, cans_free: 0, bottles_paid: 0, bottles_free: 0 };
    lines.forEach(function (line) {
      var kind = line.kind === 'bottle' ? 'bottles' : 'cans';
      counts[kind + '_free'] += line.free;
      counts[kind + '_paid'] += line.quantity - line.free;
    });

    var money = null;
    for (var i = 0; i < this.steps.length; i += 1) {
      if (this.steps[i].mode === 'summary') money = this.steps[i].money;
    }
    money = money || {};

    var paid = this.paidStep();
    var cans = paid ? paid.total() : 0;
    var types = this.canTypes();

    this.track('checkout', {
      cans_paid: counts.cans_paid,
      cans_free: counts.cans_free,
      bottles_paid: counts.bottles_paid,
      bottles_free: counts.bottles_free,
      /* Kinds, not counts — see canTypes. The joined copy is for Converge's
         filters, which take a string more readily than a list. */
      can_types: types.all,
      can_types_paid: types.paid,
      can_types_free: types.free,
      can_types_count: types.all.length,
      can_types_list: types.all.join(', '),
      can_variant_ids: types.ids,
      line_count: lines.length,
      tier_reached: this.reachedCount(cans),
      discount_percent: money.percent || 0,
      subtotal: (money.list || 0) / 100,
      saved: (money.saved || 0) / 100,
      total: (money.total || 0) / 100,
      /* `total` again under the name Converge's own events use for what an
         event is worth, so a destination it forwards to — Klaviyo reads it
         as $value — gets the amount without a mapping of its own. */
      value: (money.total || 0) / 100,
      currency: this.currency(),
      checkout_url: checkoutUrl || '',
      checkout_status: checkoutUrl ? 'ok' : 'failed',
      checkout_error: checkoutUrl ? '' : String(errorCode || 'ERROR'),
    });
  };

  Picker.prototype.readBar = function () {
    this.bar = this.root.querySelector('[data-pblp-bar]');
    this.badgeTimer = null;
    if (!this.bar) return;

    if (!this.tiers.length) {
      /* A bar with nothing to count: drop it rather than leave two empty lines
         stuck to the bottom of the screen. */
      this.bar.hidden = true;
      this.bar = null;
      return;
    }

    this.barTitle = this.bar.querySelector('[data-pblp-bar-title]');
    this.barReward = this.bar.querySelector('[data-pblp-bar-reward]');
    this.barProgress = this.bar.querySelector('[data-pblp-bar-progress]');
    this.barLabel = this.bar.querySelector('[data-pblp-bar-label]');
    this.barUnlocked = this.bar.querySelector('[data-pblp-bar-unlocked]');
    this.barBadge = this.bar.querySelector('[data-pblp-bar-badge]');
    this.barDots = Array.prototype.slice.call(this.bar.querySelectorAll('[data-pblp-dot]'));

    this.barSubtotal = this.bar.querySelector('[data-pblp-bar-subtotal]');
    this.barTotal = this.bar.querySelector('[data-pblp-bar-total]');
    this.barSaved = this.bar.querySelector('[data-pblp-bar-saved]');
    this.barSavedRow = this.bar.querySelector('[data-pblp-bar-saved-row]');
    this.checkout = this.bar.querySelector('[data-pblp-checkout]');
    this.error = this.bar.querySelector('[data-pblp-error]');

    /* The row Liquid rendered is the first step's, grouped by tier. Recording
       what it holds keeps the controller from throwing away correct markup on
       its first paint. */
    var first = this.root.querySelector('[data-pblp-step]');
    this.rowKey = first ? 'tiers:' + (parseInt(first.getAttribute('data-pblp-max'), 10) || 0) : '';
  };

  /* Writes the whole bar in one go, so a step cannot leave half of another
     step's message behind when the visitor scrolls between them. */
  Picker.prototype.paint = function (view) {
    if (!this.bar) return;

    this.bar.setAttribute('data-state', view.state);
    if (this.barTitle) this.barTitle.textContent = view.title || '';
    if (this.barReward) this.barReward.textContent = view.reward || '';
    if (this.barLabel) this.barLabel.textContent = view.label || '';

    this.barDots.forEach(function (dot) {
      var index = parseInt(dot.getAttribute('data-pblp-dot'), 10);
      dot.classList.toggle('is-on', index <= view.on);
    });

    if (this.barProgress) {
      this.barProgress.setAttribute(
        'aria-label',
        view.progress || fill(this.copy.progressLabel, { count: view.on, max: view.max })
      );
    }

    if (this.barUnlocked) {
      this.barUnlocked.textContent = view.value || '';
      this.barUnlocked.classList.toggle('is-empty', !!view.empty);
    }
  };

  /* One dot per can up to the cap, grouped by tier so the row reads as the
     ladder. Rebuilt only when the shape changes: repainting is a class toggle,
     and replacing the row on every tap would restart its transition. */
  Picker.prototype.buildTierRow = function (max) {
    var key = 'tiers:' + max;
    if (!this.barProgress || this.rowKey === key) return;
    this.rowKey = key;

    var groups = document.createDocumentFragment();
    var previous = 0;

    var group = function (span) {
      var box = document.createElement('span');
      box.className = 'pb-liquid-picker__bar-group';
      for (var i = 1; i <= span; i += 1) {
        var dot = document.createElement('span');
        dot.className = 'pb-liquid-picker__bar-dot';
        dot.setAttribute('data-pblp-dot', String(previous + i));
        box.appendChild(dot);
      }
      groups.appendChild(box);
      previous += span;
    };

    this.tiers.forEach(function (tier) {
      var span = Math.min(tier.cans, max) - previous;
      if (span > 0) group(span);
    });
    /* A cap above the last tier still gets its cans: the visitor can buy past
       the ladder, and the row has to account for every one. */
    if (max > previous) group(max - previous);

    this.barProgress.replaceChildren(groups);
    this.barDots = Array.prototype.slice.call(
      this.barProgress.querySelectorAll('[data-pblp-dot]')
    );
  };

  /* As long as the entitlement, one dot per free can, ungrouped — there are no
     tiers inside a handful of free cans. */
  Picker.prototype.buildFreeRow = function (length) {
    var key = 'free:' + length;
    if (!this.barProgress || this.rowKey === key) return;
    this.rowKey = key;

    var group = document.createElement('span');
    group.className = 'pb-liquid-picker__bar-group';
    for (var i = 1; i <= length; i += 1) {
      var dot = document.createElement('span');
      dot.className = 'pb-liquid-picker__bar-dot';
      dot.setAttribute('data-pblp-dot', String(i));
      group.appendChild(dot);
    }

    this.barProgress.replaceChildren(group);
    this.barDots = Array.prototype.slice.call(group.children);
  };

  /* One dot per bottle in the package, the free ones in the colour of a reward
     and the rest in the colour of a pick. No cap here, so the row wraps rather
     than running off the bar. */
  Picker.prototype.buildBottleRow = function (total, free) {
    var key = 'bottles:' + total + ':' + free;
    if (!this.barProgress || this.rowKey === key) return;
    this.rowKey = key;

    var group = document.createElement('span');
    group.className = 'pb-liquid-picker__bar-group pb-liquid-picker__bar-group--wrap';
    for (var i = 1; i <= total; i += 1) {
      var dot = document.createElement('span');
      dot.className =
        'pb-liquid-picker__bar-dot is-on' +
        (i <= free ? ' pb-liquid-picker__bar-dot--free' : '');
      dot.setAttribute('data-pblp-dot', String(i));
      group.appendChild(dot);
    }

    this.barProgress.replaceChildren(group);
    this.barDots = Array.prototype.slice.call(group.children);
  };

  /* The "+1". Says what just went in, not what the basket holds, so it is the
     change since the last paint — and only ever an increase: taking a can back
     out is not something to celebrate. */
  Picker.prototype.flashBadge = function (added) {
    if (!this.barBadge || this.badgeMs <= 0) return;

    this.barBadge.textContent = fill(this.copy.badge, { count: added });
    this.barBadge.classList.add('is-visible');

    var self = this;
    window.clearTimeout(this.badgeTimer);
    this.badgeTimer = window.setTimeout(function () {
      self.barBadge.classList.remove('is-visible');
    }, this.badgeMs);
  };

  /* --------------------------------------------------------------------------
     Tier arithmetic — shared by every step, since the tiers are the section's
     -------------------------------------------------------------------------- */

  /* How many tiers this many cans has cleared. Doubles as the index of the
     next one, since the tiers are sorted. */
  Picker.prototype.reachedCount = function (total) {
    if (!this.tiers || !this.tiers.length) return 0;
    var count = 0;
    for (var i = 0; i < this.tiers.length; i += 1) {
      if (total >= this.tiers[i].cans) count = i + 1;
    }
    return count;
  };

  /* What a tier adds on top of the one below it. */
  Picker.prototype.step = function (index) {
    var tier = this.tiers[index];
    var below = index > 0 ? this.tiers[index - 1] : { bottles: 0, freeCans: 0 };

    /* A perk only belongs to the step that first grants it. A discount that
       is entered on every tier from there up would otherwise be promised
       again at the next one. */
    var perk = tier.perk || '';
    if (perk) {
      for (var i = 0; i < index; i += 1) {
        if (this.tiers[i].perk === perk) {
          perk = '';
          break;
        }
      }
    }

    return {
      bottles: tier.bottles - below.bottles,
      cans: tier.freeCans - below.freeCans,
      hadBottles: below.bottles,
      hadCans: below.freeCans,
      perk: perk,
    };
  };

  Picker.prototype.countNoun = function (count, one, many) {
    return count + ' ' + this.noun(count, one, many);
  };

  Picker.prototype.noun = function (count, one, many) {
    return count === 1 ? one : many;
  };

  /* The theme editor trims a text setting on every save, so a separator cannot
     carry its own spaces — it would come back bare and a list would read
     "2 flessen·2 blikjes". The setting holds the glyph; the air around it
     belongs here. */
  Picker.prototype.glue = function () {
    var glyph = String(this.copy.separator == null ? '·' : this.copy.separator).trim();
    return glyph ? ' ' + glyph + ' ' : ' ';
  };

  /* How many kinds of can are in the basket, across every step that sells or
     hands them out. One bottle each is the recommendation a bottles step makes,
     and it is the reason it makes one at all. */
  Picker.prototype.varieties = function () {
    var seen = {};
    this.steps.forEach(function (step) {
      if (step.mode === 'bottles') return;
      Object.keys(step.counts).forEach(function (id) {
        if (step.counts[id] > 0) seen[id] = true;
      });
    });
    return Object.keys(seen).length;
  };

  /* --------------------------------------------------------------------------
     Money

     Every amount on the page is worked out here from the products' own prices
     in cents, so the format is the picker's rather than Liquid's `money`
     filter — which cannot run after the visitor has changed anything.
     -------------------------------------------------------------------------- */

  Picker.prototype.money = function (cents) {
    var value = Math.round(cents || 0) / 100;
    var whole = Math.floor(Math.abs(value));
    var part = Math.round((Math.abs(value) - whole) * 100);

    var grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    var text = (value < 0 ? '-' : '') + grouped + ',' + String(part).padStart(2, '0');
    return fill(this.copy.money || '€ {amount}', { amount: text });
  };

  /* One line of the overview: the product's own thumbnail, its name, how the
     count is made up, and the count. */
  Picker.prototype.summaryRow = function (row, kind) {
    var copy = this.copy;
    var line = document.createElement('div');
    line.className = 'pb-liquid-picker__summary-row';

    var media = document.createElement('span');
    media.className = 'pb-liquid-picker__summary-media';
    if (row.image) {
      var thumb = row.image.cloneNode(true);
      thumb.removeAttribute('sizes');
      thumb.removeAttribute('srcset');
      thumb.setAttribute('loading', 'lazy');
      thumb.setAttribute('alt', '');
      media.appendChild(thumb);
    }

    var body = document.createElement('span');
    body.className = 'pb-liquid-picker__summary-body';

    var title = document.createElement('span');
    title.className = 'pb-liquid-picker__summary-title';
    title.textContent = row.title;
    body.appendChild(title);

    var bottles = kind === 'bottles';
    var template;
    if (row.paid > 0 && row.free > 0) template = bottles ? copy.sumBottleBoth : copy.sumCanBoth;
    else if (row.free > 0) template = bottles ? copy.sumBottleFree : copy.sumCanFree;
    else template = bottles ? copy.sumBottlePaid : copy.sumCanPaid;

    var made = document.createElement('span');
    made.className = 'pb-liquid-picker__summary-made';
    made.textContent = fill(template, { paid: row.paid, free: row.free });
    body.appendChild(made);

    var count = document.createElement('span');
    count.className = 'pb-liquid-picker__summary-count';
    count.textContent = '× ' + (row.paid + row.free);

    line.appendChild(media);
    line.appendChild(body);
    line.appendChild(count);
    return line;
  };

  Picker.prototype.valueRow = function (label, amount, kind) {
    var group = document.createDocumentFragment();

    var dt = document.createElement('dt');
    dt.className = 'pb-liquid-picker__value-label pb-liquid-picker__value-label--' + kind;
    dt.textContent = label;

    var dd = document.createElement('dd');
    dd.className = 'pb-liquid-picker__value-amount pb-liquid-picker__value-amount--' + kind;
    dd.textContent = this.money(amount);

    group.appendChild(dt);
    group.appendChild(dd);
    return group;
  };

  /* Substitutes into copy that carries its own markup — a rich text setting
     where the merchant decided which half of the sentence is bold. The values
     are escaped; the template's own tags are not. */
  Picker.prototype.fillRich = function (template, values) {
    return braces(template).replace(/\{(\w+)\}/g, function (match, key) {
      if (!Object.prototype.hasOwnProperty.call(values, key)) return match;
      return escapeHtml(values[key]);
    });
  };

  /* "een gratis fles" reads better than "1 gratis fles" — but only where it is
     the whole reward. In a list the numbers should line up, and "een extra fles
     + 2 blikjes" mixes a word and a digit in one breath, so the article is for
     the single-kind phrase alone. The list of what is already in says "1 fles"
     either way, which is why this is not in countNoun(). */
  Picker.prototype.amount = function (count) {
    return count === 1 && this.copy.wordOne ? this.copy.wordOne : String(count);
  };

  Picker.prototype.rewardPhrase = function (step) {
    var copy = this.copy;
    var phrase = '';

    if (step.bottles > 0 && step.cans > 0) {
      /* Two kinds at once. A single bottle drops its digit — "extra fles + 2
         blikjes" is what fits the bar on one line, and a bare "1" in front of
         a word reads as counted rather than given. Two or more keep the
         number, because that is a number the visitor cannot work out. */
      phrase = fill(step.bottles === 1 ? copy.rewardComboOne : copy.rewardCombo, {
        bottles: step.bottles,
        bottleWord: step.bottles === 1 ? copy.bottleOne : copy.bottleMany,
        cans: step.cans,
        canWord: step.cans === 1 ? copy.canOne : copy.canMany,
      });
    } else if (step.bottles > 0) {
      phrase = fill(step.hadBottles > 0 ? copy.rewardSoloMore : copy.rewardSolo, {
        count: this.amount(step.bottles),
        noun: step.bottles === 1 ? copy.bottleOne : copy.bottleMany,
      });
    } else if (step.cans > 0) {
      phrase = fill(step.hadCans > 0 ? copy.rewardSoloMore : copy.rewardSolo, {
        count: this.amount(step.cans),
        noun: step.cans === 1 ? copy.canOne : copy.canMany,
      });
    }

    if (step.perk) {
      phrase = phrase
        ? fill(copy.rewardPerk, { reward: phrase, perk: step.perk })
        : step.perk;
    }

    return phrase;
  };

  /* --------------------------------------------------------------------------
     Which step is in view

     The bar sits at the bottom of the screen and the steps stack, so the step
     that owns it is the last one whose top has passed the middle of the
     viewport. Cheap, and it never leaves the bar between two steps.
     -------------------------------------------------------------------------- */

  Picker.prototype.owner = function () {
    return this.active;
  };

  Picker.prototype.visible = function () {
    return this.steps.filter(function (step) {
      return step.shown();
    });
  };

  /* Numbers the steps and names the seams, both from the steps that are
     actually on the page.

     Neither can be written in Liquid, which only knows the block order: a free
     step hides itself when the paid step has unlocked nothing, and then the
     steps under it are one lower than their block position — a bottles step
     printing STAP 3 while it is the second thing the visitor sees. The seam has
     the same problem from the other side: it names the step behind it, and a
     hidden step in between would have it crediting a step that is not there.
     The first step on the page must not open with a seam at all. */
  Picker.prototype.paintLabels = function () {
    var previous = null;
    var self = this;

    this.visible().forEach(function (step, index) {
      var label = fill(step.eyebrowPattern, { n: index + 1 });
      if (label !== step.eyebrow) {
        step.eyebrow = label;
        step.eyebrowEls.forEach(function (el) {
          el.textContent = label;
        });
      }

      if (step.seam) {
        /* An overview step closes every step at once, so it says so in words
           of its own rather than naming the one above it. */
        /* Which pill: the overview's own words; under a locked step, words that
           do not say "afgerond" about a step nobody has started ("STAP 2 NOG
           NIET VERDIEND"); otherwise the step behind it, rounded off. */
        var template = self.copy.seamLabel;
        if (step.mode === 'summary') template = self.copy.seamFinal;
        else if (previous && previous.locked) template = self.copy.seamLocked;

        var name = previous && previous.eyebrow;
        var show = !!name && !!template;

        /* Above a locked step the pill still says "STAP 1 AFGEROND" — Brent
           wants the divider there too — but only once step 1 holds something.
           With an empty basket nothing was rounded off, and the locked step
           simply starts under the first. */
        if (step.locked && previous && previous.total() <= 0) show = false;

        /* This runs on every scroll frame, so it only touches what changed:
           a same-value write is cheap but not free, and a write right before
           `refreshActive` measures is the one thing that can make that
           measurement force a layout. */
        if (step.seam.hidden !== !show) step.seam.hidden = !show;
        step.root.classList.toggle('pb-liquid-picker__step--seamed', show);
        if (show && step.seamPill) {
          var pill = fill(template, { step: name });
          if (step.seamPill.textContent !== pill) step.seamPill.textContent = pill;
        }
      }

      previous = step;
    });
  };

  Picker.prototype.refreshActive = function () {
    this.paintLabels();

    /* A locked free step never owns the bar: its free-bar would read "all
       free cans chosen" about a cap of nought. Scrolling through it the bar
       keeps the paid step's line — "Nog 3 blikjes voor 2 gratis blikjes" —
       which is exactly what a locked step is asking for. */
    var visible = this.visible().filter(function (step) {
      return !step.locked;
    });

    var found = null;
    if (visible.length) {
      var middle = window.innerHeight / 2;
      found = visible[0];
      visible.forEach(function (step) {
        if (step.root.getBoundingClientRect().top <= middle) found = step;
      });
    }

    /* The incoming step writes the whole bar, row included, so nothing of the
       outgoing one survives the handover. */
    if (this.setActive(found) && found) found.paintBar();
  };

  /* Assigns the step and returns whether it changed. Painting is left to the
     caller: a tap makes its own step active and then renders anyway, and
     painting here as well would do the bar twice. */
  Picker.prototype.setActive = function (step) {
    if (this.active === step) return false;
    this.active = step;
    if (this.bar) this.bar.hidden = !step;
    return true;
  };

  /* --------------------------------------------------------------------------
     Controls
     -------------------------------------------------------------------------- */

  Picker.prototype.paintControl = function (control, quantity, full, floored, shown) {
    if (!control) return;

    /* What the visitor is being counted, which is not always what the basket
       holds — see Step.render. A bottles basket carries the free bottles as its
       floor, so a step that gave three away holds three before the visitor has
       chosen anything. Which face the control shows follows THAT number and not
       the basket's: on the basket's the question would never be asked, because
       the card would open on a stepper reading nought. */
    var counted = shown === undefined ? quantity : shown;

    control.setAttribute('data-state', counted > 0 ? 'count' : 'add');

    var count = control.querySelector('[data-pblp-count]');
    if (count) count.textContent = String(counted);

    var add = control.querySelector('[data-pblp-add]');
    if (add) add.disabled = full;

    var increase = control.querySelector('[data-pblp-increase]');
    if (increase) increase.disabled = full;

    /* Only a bottles step has a floor, and there the minus stops at the
       bottles the ladder already gave away. */
    var decrease = control.querySelector('[data-pblp-decrease]');
    if (decrease) decrease.disabled = !!floored;
  };

  /* The sheet is one panel for every step, so its button is labelled and
     counted by the step whose card is open — "Toevoegen" in a paid step,
     "Gratis kiezen" in a free one. */
  Picker.prototype.paintSheetControl = function () {
    if (!this.openCard) return;

    var step = this.stepOf(this.openCard);
    if (!step) return;

    if (this.sheetAdd) this.sheetAdd.textContent = step.addLabel;

    var id = this.openCard.getAttribute('data-pblp-variant');
    var quantity = step.counts[id] || 0;

    /* Same reading as on the card behind it: in a bottles step the number is
       the bottles being paid for, and the minus stops at the ladder's. */
    this.paintControl(
      this.sheetControl,
      quantity,
      step.total() >= step.max,
      step.total() <= step.min,
      step.mode === 'bottles' ? quantity - step.min : quantity
    );
  };

  /* The step that owns this basket, or null. Undefined while the steps are
     still being built — `bought` falls back to storage then. */
  Picker.prototype.stepFor = function (bundleId) {
    if (!bundleId || !this.steps) return null;
    for (var i = 0; i < this.steps.length; i += 1) {
      if (this.steps[i].bundleId === bundleId) return this.steps[i];
    }
    return null;
  };

  Picker.prototype.stepOf = function (element) {
    var root = element.closest('[data-pblp-step]');
    if (!root) return null;
    for (var i = 0; i < this.steps.length; i += 1) {
      if (this.steps[i].root === root) return this.steps[i];
    }
    return null;
  };

  /* --------------------------------------------------------------------------
     Deep link: ?can=shampoo

     A link can land the visitor on the cans with one product sheet already
     open. The key after `can=` is matched against every card in a step that is
     on the page, in page order, and the first hit wins — so a product that
     stands in the paid step and again in the free step opens from the paid one.
     A card matches when the key equals its product handle, its title, its
     eyebrow + title, or its variant id (all slugified: `Body Wash` → `body-wash`),
     or — weaker, only when nothing matched exactly — when the key is a run of
     the handle's hyphenated parts: `shampoo` finds `wondr-liquid-shampoo-rosy-rice`,
     `blossom-bliss` finds `wondr-liquid-body-wash-blossom-bliss`.

     It runs at `load`, not when the picker is built. On desktop the desk layer
     (pb-liquids-desk.js, deferred and later in the document) moves the sheet
     into the hero after this script has run; a sheet opened before that move
     would be modal, `holdPage` would inert every sibling on the way up to
     <body> — the hero included — and the sheet would then be moved into an
     inert box nobody can click. `load` fires after every deferred script and
     every DOMContentLoaded handler, so by then the page is laid out the way the
     visitor sees it, on both layouts.

     Not in the theme editor: a merchant with `?can=` in the preview address
     would get a sheet in the way on every reload.
     -------------------------------------------------------------------------- */

  var deepLinked = false;

  function slug(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /* Whether `needle`'s hyphenated parts appear in `haystack`'s, in a row. */
  function partsContain(haystack, needle) {
    var h = haystack.split('-');
    var n = needle.split('-');
    if (!n.length || n.length > h.length) return false;
    for (var i = 0; i + n.length <= h.length; i += 1) {
      var hit = true;
      for (var j = 0; j < n.length; j += 1) {
        if (h[i + j] !== n[j]) {
          hit = false;
          break;
        }
      }
      if (hit) return true;
    }
    return false;
  }

  Picker.prototype.findCard = function (key) {
    var wanted = slug(key);
    if (!wanted) return null;

    var partial = null;
    for (var s = 0; s < this.steps.length; s += 1) {
      var step = this.steps[s];
      if (step.root.hidden || !step.onPage()) continue;

      var cards = step.root.querySelectorAll('[data-pblp-card]');
      for (var c = 0; c < cards.length; c += 1) {
        var card = cards[c];
        var handle = slug(card.getAttribute('data-pblp-handle'));
        var title = slug(card.getAttribute('data-pblp-title'));
        var full = slug(card.getAttribute('data-pblp-eyebrow') + ' ' + card.getAttribute('data-pblp-title'));
        var variant = String(card.getAttribute('data-pblp-variant') || '');

        if (wanted === handle || wanted === title || wanted === full || wanted === variant) return card;
        if (!partial && handle && partsContain(handle, wanted)) partial = card;
      }
    }
    return partial;
  };

  Picker.prototype.deepLink = function () {
    if (deepLinked) return;
    if (window.Shopify && window.Shopify.designMode) return;

    var key = '';
    try {
      key = new URLSearchParams(window.location.search).get('can') || '';
    } catch (error) {
      return;
    }
    if (!key.trim()) return;
    deepLinked = true;

    var self = this;
    var go = function () {
      /* After the fonts, then two frames. The desk layer re-measures the nav
         and re-lays the column the moment `document.fonts.ready` resolves
         (its handler was registered first, so it runs first), and Neulis Neue
         changes line heights everywhere on the phone; a scroll measured
         before that landed 40 to 60 px off once the fonts came in. */
      var fonts = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
      fonts.then(function () {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            if (!self.root.isConnected) return;
            var card = self.findCard(key);
            if (!card) return;
            self.reveal(card);
            self.open(card);
          });
        });
      });
    };

    if (document.readyState === 'complete') {
      go();
    } else {
      window.addEventListener('load', go, { once: true });
    }
  };

  /* Puts the card's step on screen before its sheet opens, so closing the
     sheet leaves the visitor at the cans and not at the top of the page. */
  Picker.prototype.reveal = function (card) {
    var step = this.stepOf(card);
    var target = step ? step.root : card;
    var air = 12;

    /* How far the page has to move so the step sits where it should. Zero
       when it already does. Read fresh on every call: the page keeps settling
       for a moment after `load` (fonts, a header bar that renders late, lazy
       images), and each shift moves the goal. */
    var offBy = function () {
      /* Desktop: the picker stands in the pinned column, whose content follows
         the page scroll one to one (pb-liquids-desk.js `sync`), so moving the
         page by the distance between the step and the column's top edge puts
         the step at the top of the column. The whole step when it fits —
         heading and all four cans (1543 × 900); when the column is lower than
         that (a laptop at 800), the cans win over the heading: the grid goes
         to the top of the column and the heading sits just above it, out of
         view. Not `focus()`: the layer's own focusin handler scrolls just far
         enough to show the focused button, which left the first can half in
         view and the rest below the fold.

         "Desktop" is the column being pinned — `position: sticky` from the
         desk stylesheet — and not the wrapper being there: the desk section's
         bootstrap wraps the picker into its column at every width, phone
         included, while its stylesheet only applies above the breakpoint. On
         a phone the wrapper is an ordinary box in the flow. */
      var panel = target.closest('[data-pbdk-panel]');
      var style = panel && window.getComputedStyle(panel);
      if (style && style.position === 'sticky') {
        var seen = panel.getBoundingClientRect();
        var here = target.getBoundingClientRect();
        var grid = target.querySelector('.pb-liquid-picker__grid');
        var anchor = grid && here.height + 2 * air > seen.height ? grid.getBoundingClientRect() : here;

        /* Two parts. Near the top of the page the column is not pinned yet:
           the first pixels of scroll move the whole composition up to the
           column's sticky `top` without moving anything inside it. Then, while
           pinned, one page pixel is one content pixel — but only for as long
           as the track lasts (`--pbdk-travel`, set by the layer on the track;
           how far the content has gone is the `top` the layer writes on the
           scroll box). Past the track the column lets go and scrolls away
           with the page, so the move is clamped to what is left of it: on a
           900 px screen the grid cannot reach the top of the column at all,
           and what remains is the end of the track — the four cans at the
           bottom of the column, the heading cut at the top. That is the best
           that column can show. */
        var pinnedTop = parseFloat(style.top);
        var toPin = isNaN(pinnedTop) ? 0 : Math.max(0, seen.top - pinnedTop);
        var want = anchor.top - seen.top - air;

        var track = panel.closest('[data-pbdk-track]');
        var box = panel.querySelector('[data-pbdk-scroll]');
        var travel = track ? parseFloat(window.getComputedStyle(track).getPropertyValue('--pbdk-travel')) : NaN;
        var gone = box ? -parseFloat(box.style.top) || 0 : 0;
        if (!isNaN(travel)) want = Math.min(want, travel - gone);
        want = Math.max(want, -gone);

        return toPin + want;
      }

      /* Phone: the step's top under the floating nav, with a little air. The
         nav is `position: fixed`, so the flow knows nothing of it; measured
         the way pb-liquids-desk.js does, offsetTop and not a rect, because the
         bar slides out of view with a transform that a rect would count. */
      var top = 0;
      var floating = document.querySelector('.pb-unified-nav__fixed');
      var header = document.getElementById('header-group');
      if (floating && floating.offsetHeight) {
        top = floating.offsetTop + floating.offsetHeight;
      } else if (header) {
        top = header.getBoundingClientRect().height;
      }
      return target.getBoundingClientRect().top - top - air;
    };

    var place = function () {
      var delta = Math.round(offBy());
      if (Math.abs(delta) < 2) return;
      jump(function () {
        window.scrollBy(0, delta);
      });
    };

    place();

    /* And again, a few times over the next moments, while the page is still
       moving under it — measured 40 to 60 px off on the first pass when a
       header bar or the fonts landed after the scroll. Stops as soon as the
       page is no longer where this left it: then the visitor scrolled, and
       the visitor wins. */
    var tries = 0;
    var expected = window.pageYOffset;
    var check = function () {
      if (Math.abs(window.pageYOffset - expected) > 2) return;
      var before = Math.round(offBy());
      place();
      /* A correction that changed nothing means the scroll is not reaching
         the step (the page has run out, or the column has let go): stop,
         rather than push the page further each time. */
      if (Math.abs(before) >= 2 && Math.abs(Math.round(offBy()) - before) < 2) return;
      expected = window.pageYOffset;
      tries += 1;
      if (tries < 6) window.setTimeout(check, 250);
    };
    window.setTimeout(check, 250);
  };

  /* Scrolls at once, not smoothly. base.css puts `scroll-behavior: smooth` on
     the page, and a landing should not glide; on a phone `open` also locks
     the page scroll right after, which would cut a glide off halfway. The
     `behavior: 'instant'` option would say the same, but older engines throw
     on a value they do not know and the sheet would never open — so the
     property is switched off around the call instead, and put back. */
  function jump(scroll) {
    var root = document.documentElement;
    var before = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    scroll();
    root.style.scrollBehavior = before;
  }

  /* --------------------------------------------------------------------------
     Product sheet
     -------------------------------------------------------------------------- */

  /* Templates are inert: nothing in them downloads until it is cloned into
     the document. The sheet's first shot therefore used to start on the click
     that opened it, and the visitor stared at an empty frame until that
     finished. Warm the first frame early — on hover, focus, touch, or idle —
     so a click usually finds it already in cache. Desktop only shows that
     first frame; later ones stay lazy. */
  Picker.prototype.warmSheet = function (card) {
    if (!card || card.getAttribute('data-pblp-sheet-warm') === '1') return;

    var template = card.querySelector('[data-pblp-sheet-content]');
    if (!template) return;

    var img = template.content.querySelector('.pb-liquid-picker__sheet-frame img');
    if (!img) return;

    var src = img.getAttribute('src');
    if (!src) return;

    card.setAttribute('data-pblp-sheet-warm', '1');

    var warm = new Image();
    var srcset = img.getAttribute('srcset');
    var sizes = img.getAttribute('sizes');
    if (srcset) warm.srcset = srcset;
    if (sizes) warm.sizes = sizes;
    warm.decoding = 'async';
    warm.src = src;
  };

  /* After the template is cloned, lazy loading still waits a layout pass.
     The first frame is on screen immediately, so fetch it as a high-priority
     eager image and kick decode so paint does not wait on the next tick. */
  Picker.prototype.primeSheetImages = function () {
    if (!this.sheetSlot) return;

    var images = this.sheetSlot.querySelectorAll('.pb-liquid-picker__sheet-frame img');
    Array.prototype.forEach.call(images, function (img, index) {
      img.loading = 'eager';
      if (index !== 0) return;
      img.setAttribute('fetchpriority', 'high');
      if (typeof img.decode === 'function') {
        img.decode().catch(function () {});
      }
    });
  };

  /* First open step first: that is what the visitor sees beside the hero, and
     warming every card on the page would fight the hero for bandwidth. */
  Picker.prototype.warmIdle = function () {
    if (this.sheetWarmQueued) return;

    var self = this;
    var step = null;
    for (var i = 0; i < this.steps.length; i += 1) {
      if (!this.steps[i].root.hidden) {
        step = this.steps[i];
        break;
      }
    }
    if (!step) return;

    this.sheetWarmQueued = true;

    var cards = Array.prototype.slice.call(step.root.querySelectorAll('[data-pblp-card]'));
    var index = 0;
    var tick = function (deadline) {
      while (index < cards.length) {
        if (deadline && deadline.timeRemaining() < 8 && !deadline.didTimeout) break;
        self.warmSheet(cards[index]);
        index += 1;
      }
      if (index < cards.length) schedule(tick);
    };
    var schedule = window.requestIdleCallback
      ? function (fn) {
          window.requestIdleCallback(fn, { timeout: 2500 });
        }
      : function (fn) {
          window.setTimeout(function () {
            fn({ timeRemaining: function () { return 12; }, didTimeout: true });
          }, 200);
        };
    schedule(tick);
  };

  Picker.prototype.open = function (card) {
    if (!this.sheet || !this.sheetSlot) return;
    if (!card.querySelector('[data-pblp-sheet-content]')) return;

    /* A close still fading out is over now: whatever it was going to do
       (hide, empty the slot) would land on the sheet being opened. */
    this.settleClose();

    this.lastFocus = document.activeElement;

    /* A swipe's slide-in that a close cut short must not leave the panel
       shifted and see-through for the next open. */
    this.sheetSlot.classList.remove('is-paging');
    this.sheetSlot.style.transform = '';
    this.sheetSlot.style.opacity = '';

    /* Fills the panel and says which can is in it — the same call a swipe to
       the next can makes. */
    this.fillSheet(card);

    this.sheet.hidden = false;
    this.backdrop.hidden = false;
    /* Two frames: the element has to be laid out before the transition can
       run from its closed state, and one frame is not always enough on iOS.
       Kept on the instance so a close in the meantime can cancel them —
       otherwise they would put `is-open` back on a sheet that is closing. */
    var self = this;
    this.openFrame = requestAnimationFrame(function () {
      self.openFrame = requestAnimationFrame(function () {
        self.openFrame = 0;
        self.sheet.classList.add('is-open');
        self.backdrop.classList.add('is-open');
        /* Focus goes to the close button one frame LATER and not before: the
           sheet is `visibility: hidden` until `is-open`, and visibility is
           part of the transition — at the frame the class lands the computed
           value is still the start value, hidden, and a hidden element cannot
           take focus. The call used to land on <body>, and the first Tab went
           wherever the browser liked. */
        self.openFrame = requestAnimationFrame(function () {
          self.openFrame = 0;
          var close = self.sheet.querySelector('[data-pblp-close]');
          if (close) close.focus({ preventScroll: true });
        });
      });
    });

    document.documentElement.classList.add('pb-liquid-picker-sheet-open');

    /* A MODAL IS MODAL. On a phone the sheet covers the page, so the page
       goes out of reach: every element beside the sheet's ancestors gets
       `inert` (no focus, no clicks, hidden from assistive tech), and Tab
       wraps inside the sheet (`onKeydown`). Without that a keyboard user
       tabbed from the close button straight onto the cards behind the
       backdrop while the sheet was still open. On desktop the layer moves
       the sheet into the hero as a panel beside a page that stays usable —
       there it is not modal and says so. */
    var modal = !this.sheet.closest('[data-pbdk-sheet-host]');
    this.sheet.setAttribute('aria-modal', modal ? 'true' : 'false');
    if (modal) this.holdPage();
  };

  /* Puts everything that is not the sheet (or the backdrop, which closes it)
     out of reach, level by level up to <body>. Undone by `releasePage`. */
  Picker.prototype.holdPage = function () {
    this.releasePage();
    var self = this;
    var node = this.sheet;
    while (node && node.parentNode && node !== document.body) {
      Array.prototype.forEach.call(node.parentNode.children, function (sibling) {
        if (sibling === node || sibling === self.backdrop) return;
        if (/^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(sibling.tagName)) return;
        if (sibling.inert) return;
        sibling.inert = true;
        self.inerted.push(sibling);
      });
      node = node.parentNode;
    }
  };

  Picker.prototype.releasePage = function () {
    this.inerted.forEach(function (element) {
      element.inert = false;
    });
    this.inerted = [];
  };

  /* Everything in the sheet a Tab can land on, in order. */
  Picker.prototype.sheetFocusables = function () {
    var selector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
      'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.prototype.filter.call(this.sheet.querySelectorAll(selector), function (el) {
      return el.getClientRects().length > 0;
    });
  };

  /* Forgets the close in flight, if any: its timer, its listener, and the
     frames of an open that had not landed yet. Nothing of either will run. */
  Picker.prototype.settleClose = function () {
    this.closeToken += 1;
    if (this.closeTimer) window.clearTimeout(this.closeTimer);
    this.closeTimer = 0;
    if (this.closeListener && this.sheet) {
      this.sheet.removeEventListener('transitionend', this.closeListener);
    }
    this.closeListener = null;
    if (this.openFrame) cancelAnimationFrame(this.openFrame);
    this.openFrame = 0;
  };

  Picker.prototype.close = function () {
    if (!this.sheet || this.sheet.hidden) return;

    /* One close at a time. The previous one's timer and listener go, and an
       open still waiting on its frames goes with them. */
    this.settleClose();

    this.sheet.classList.remove('is-open');
    this.backdrop.classList.remove('is-open');
    document.documentElement.classList.remove('pb-liquid-picker-sheet-open');
    this.releasePage();

    /* THE SAME CLEANUP IS ARMED TWICE — on transitionend and on a timer — and
       only the first may run. The timer was never cancelled when the fade
       finished first (the fade is 240ms, the timer 400ms, so it always did),
       and it fired into whatever the sheet was doing 400ms later: open a
       second card within that window and the old timer hid it and emptied
       its slot. And transitionend bubbles, so a stepper inside the sheet
       finishing its own transition used to count as the sheet's fade. So:
       the sheet's own transition only, and only while this close is still
       the current one. */
    var self = this;
    var token = this.closeToken;
    var done = function (event) {
      if (event && event.target !== self.sheet) return;
      if (self.closeToken !== token) return;
      self.settleClose();
      self.sheet.hidden = true;
      self.backdrop.hidden = true;
      self.sheetSlot.replaceChildren();
      self.openCard = null;
    };
    /* Wait out the fade, but never hang on a transitionend that a reduced-
       motion setting or a background tab may never fire. */
    this.closeListener = done;
    this.sheet.addEventListener('transitionend', done);
    this.closeTimer = window.setTimeout(done, 400);

    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
    this.lastFocus = null;
  };

  /* --------------------------------------------------------------------------
     The sheet as a pager over the step's cans

     ONE PANEL, EVERY CAN OF THE STEP. The dots under the sheet are the cans of
     the step it was opened from, and a swipe hands the panel to the next one:
     the visitor goes through the range without closing the sheet, tapping the
     next can and waiting for it to open again.

     A can's own photos still swipe inside the photo strip — see `buildShots`,
     whose dots sit on the artwork — so the two gestures never mean the same
     thing in the same place.

     Phone only. On desktop the layer shows the sheet as a panel beside the
     grid, where the next can is one click away in the page itself; there the
     dots are hidden and none of this binds.
     -------------------------------------------------------------------------- */

  Picker.prototype.pages = function () {
    return !!this.sheet && !this.sheet.closest('[data-pbdk-sheet-host]');
  };

  /* The cans this sheet can page through: the ones in the step whose card is
     open. Nothing to page through in a step of one. */
  Picker.prototype.pagerCards = function () {
    if (!this.openCard || !this.pages()) return [];
    var step = this.stepOf(this.openCard);
    if (!step || step.cards.length < 2) return [];
    return step.cards;
  };

  /* Rebuilt only when the step behind the sheet changes — the active dot
     stretches into its pill on a transition, and replacing the row on every
     swipe would restart it. */
  Picker.prototype.buildDots = function () {
    if (!this.dots) return;

    var cards = this.pagerCards();
    var step = this.openCard && this.stepOf(this.openCard);
    var key = cards.length ? (step ? step.bundleId : '') + ':' + cards.length : '';

    this.dots.hidden = !cards.length;
    if (this.dotsKey === key) {
      this.paintDots();
      return;
    }
    this.dotsKey = key;
    this.dots.replaceChildren();
    if (!cards.length) return;

    var self = this;
    cards.forEach(function (card, index) {
      var item = document.createElement('li');
      var dot = document.createElement('button');
      var name = [card.getAttribute('data-pblp-eyebrow'), card.getAttribute('data-pblp-title')]
        .filter(Boolean)
        .join(' ');
      dot.type = 'button';
      dot.className = 'pb-liquid-picker__dot';
      dot.setAttribute('aria-label', name);
      dot.addEventListener('click', function () {
        self.showCard(cards[index], index > cards.indexOf(self.openCard) ? 1 : -1);
      });
      item.appendChild(dot);
      self.dots.appendChild(item);
    });
    this.paintDots();
  };

  Picker.prototype.paintDots = function () {
    if (!this.dots || this.dots.hidden) return;

    var at = this.pagerCards().indexOf(this.openCard);
    Array.prototype.forEach.call(this.dots.children, function (item, i) {
      var dot = item.firstElementChild;
      if (!dot) return;
      dot.classList.toggle('is-active', i === at);
      dot.setAttribute('aria-current', i === at ? 'true' : 'false');
    });
  };

  /* One can along, round and round: past the last one comes the first again,
     and back from the first is the last. The step is a handful of cans and the
     dots stay in the panel to say which one is in it, so there is no end to
     run into — a swipe never has to be taken back to see the rest. */
  Picker.prototype.pageBy = function (direction) {
    var cards = this.pagerCards();
    var at = cards.indexOf(this.openCard);
    if (at < 0) return;
    this.showCard(cards[this.wrap(at + direction, cards.length)], direction);
  };

  /* An index onto the ring of cans. JavaScript's `%` keeps the sign of the
     left-hand side, so -1 of 4 has to be brought back into range by hand. */
  Picker.prototype.wrap = function (index, total) {
    if (!total) return 0;
    return ((index % total) + total) % total;
  };

  /* Hands the open sheet to another can: the panel keeps standing where it is
     and only its content is refilled, from that card's own template. The new
     content comes in from the side the finger came from, so the swap reads as
     the swipe finishing rather than as the sheet blinking. */
  Picker.prototype.showCard = function (card, direction) {
    if (!card || card === this.openCard) return;
    if (!this.fillSheet(card)) return;

    /* Closing now returns the visitor to the can they ended up on, not to the
       one they opened the sheet with. */
    var opener = card.querySelector('[data-pblp-open]');
    if (opener) this.lastFocus = opener;

    var slot = this.sheetSlot;
    if (this.reduced()) return;

    slot.classList.add('is-paging');
    slot.style.transform = 'translate3d(' + (direction > 0 ? 22 : -22) + '%, 0, 0)';
    slot.style.opacity = '0';
    /* Two frames, as when the sheet itself opens: the start state has to be
       laid out before the transition can run from it. */
    if (this.pageFrame) cancelAnimationFrame(this.pageFrame);
    var self = this;
    this.pageFrame = requestAnimationFrame(function () {
      self.pageFrame = requestAnimationFrame(function () {
        self.pageFrame = 0;
        slot.classList.remove('is-paging');
        slot.style.transform = '';
        slot.style.opacity = '';
      });
    });
  };

  /* Fills the panel from a card's template. Everything that is about which can
     is open lives here, so opening and paging cannot drift apart. */
  Picker.prototype.fillSheet = function (card) {
    var template = card.querySelector('[data-pblp-sheet-content]');
    if (!template) return false;

    this.warmSheet(card);
    this.sheetSlot.replaceChildren(template.content.cloneNode(true));
    this.primeSheetImages();
    this.openCard = card;

    this.buildShots();
    this.buildDots();
    this.paintSheetControl();

    /* The neighbours on the ring are one swipe away, so their first shot
       should already be in cache when the swipe lands. */
    var cards = this.pagerCards();
    var at = cards.indexOf(card);
    if (at >= 0) {
      this.warmSheet(cards[this.wrap(at - 1, cards.length)]);
      this.warmSheet(cards[this.wrap(at + 1, cards.length)]);
    }
    return true;
  };

  Picker.prototype.reduced = function () {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  };

  /* Swiping the panel from one can to the next. The finger takes the content a
     third of the way with it, so the gesture answers before it is finished,
     and on release either the next can comes in or the current one springs
     back.

     A gesture that starts in a photo strip that can scroll belongs to the
     strip: a can with several shots still swipes through its own photos. A
     gesture that is mostly vertical is the panel's own scroll, so the axis is
     settled first and nothing is taken over until the horizontal one wins. */
  Picker.prototype.bindPager = function () {
    if (!this.sheet) return;

    var self = this;
    var startX = 0;
    var startY = 0;
    var moved = 0;
    var axis = '';
    var live = false;

    var settle = function () {
      live = false;
      self.sheetSlot.classList.remove('is-paging');
      self.sheetSlot.style.transform = '';
      self.sheetSlot.style.opacity = '';
    };

    this.sheet.addEventListener(
      'touchstart',
      function (event) {
        live = false;
        if (event.touches.length !== 1 || self.pagerCards().length < 2) return;

        var strip = event.target.closest && event.target.closest('[data-pblp-track]');
        if (strip && strip.scrollWidth > strip.clientWidth) return;

        live = true;
        axis = '';
        moved = 0;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      },
      { passive: true }
    );

    this.sheet.addEventListener(
      'touchmove',
      function (event) {
        if (!live || event.touches.length !== 1) return;

        moved = event.touches[0].clientX - startX;
        var down = event.touches[0].clientY - startY;
        if (!axis) {
          if (Math.abs(moved) < 10 && Math.abs(down) < 10) return;
          axis = Math.abs(moved) > Math.abs(down) ? 'x' : 'y';
          if (axis === 'x') self.sheetSlot.classList.add('is-paging');
        }
        if (axis !== 'x' || self.reduced()) return;
        self.sheetSlot.style.transform = 'translate3d(' + moved / 3 + 'px, 0, 0)';
      },
      { passive: true }
    );

    var end = function () {
      if (!live) return;
      var over = axis === 'x' && Math.abs(moved) >= 48;
      /* The class goes first and the transform after, so what is left of the
         gesture springs back on the transition instead of jumping. */
      settle();
      if (over) self.pageBy(moved < 0 ? 1 : -1);
    };
    this.sheet.addEventListener('touchend', end);
    this.sheet.addEventListener('touchcancel', end);
  };

  /* --------------------------------------------------------------------------
     A can's own photos, inside the panel
     -------------------------------------------------------------------------- */

  /* The strip is a native scroll-snap scroller, so the dots below it only have
     to report where it landed — and take a tap to a shot, because a dot that
     does nothing reads as a gallery that is broken. They sit on the artwork
     rather than under the sheet: the row under the sheet is the step's cans. */
  Picker.prototype.buildShots = function () {
    var media = this.sheetSlot.querySelector('.pb-liquid-picker__sheet-media');
    var track = this.sheetSlot.querySelector('[data-pblp-track]');
    var frames = track
      ? Array.prototype.slice.call(track.querySelectorAll('.pb-liquid-picker__sheet-frame'))
      : [];
    if (!media || frames.length < 2) return;

    var self = this;
    var label = (this.dots && this.dots.getAttribute('data-pblp-shot-label')) || '';
    var row = document.createElement('ul');
    row.className = 'pb-liquid-picker__shots';
    row.setAttribute('role', 'list');

    frames.forEach(function (frame, index) {
      var item = document.createElement('li');
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'pb-liquid-picker__dot' + (index === 0 ? ' is-active' : '');
      dot.setAttribute('aria-label', (label + ' ' + (index + 1)).replace(/^\s+/, ''));
      dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');
      dot.addEventListener('click', function () {
        self.showShot(track, index);
      });
      item.appendChild(dot);
      row.appendChild(item);
    });
    media.appendChild(row);

    /* Once per frame: a swipe fires scroll far more often than that. */
    var queued = false;
    track.addEventListener('scroll', function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        self.paintShots(track, row);
      });
    });

    this.dragStrip(track);
  };

  Picker.prototype.paintShots = function (track, row) {
    if (!track || !track.clientWidth || !row.isConnected) return;

    var index = Math.round(track.scrollLeft / track.clientWidth);
    Array.prototype.forEach.call(row.children, function (item, i) {
      var dot = item.firstElementChild;
      if (!dot) return;
      dot.classList.toggle('is-active', i === index);
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  };

  /* Frames are the width of the strip, so a frame is a strip-width along it. */
  Picker.prototype.showShot = function (track, index) {
    if (!track) return;

    var left = index * track.clientWidth;
    if (typeof track.scrollTo === 'function') {
      track.scrollTo({ left: left, behavior: this.reduced() ? 'auto' : 'smooth' });
    } else {
      track.scrollLeft = left;
    }
  };

  /* DRAGGING THE STRIP WITH A POINTER THAT IS NOT A FINGER. A phone needs
     nothing here — the strip is a native touch scroller — but a mouse has no
     gesture for a horizontal scroller, so on a narrow desktop window (and in
     the responsive mode of a browser that does not emulate touch) the dots
     promised a swipe that could not be made. Mouse and pen only, primary
     button only, and only once the pointer has moved far enough that this is a
     drag: below the threshold nothing happens, so a plain click stays a click.

     Snapping is off while dragging (`is-dragging`) — with `x mandatory` every
     assignment to scrollLeft would snap back to the nearest frame and the
     strip would judder in place — and the frame nearest where the drag ended
     is snapped to by hand. */
  Picker.prototype.dragStrip = function (track) {
    if (!track || track.getAttribute('data-pblp-drag') === '1') return;
    track.setAttribute('data-pblp-drag', '1');

    var self = this;
    var pointer = -1;
    var startX = 0;
    var startLeft = 0;
    var dragging = false;

    var stop = function (event) {
      if (event.pointerId !== pointer) return;
      pointer = -1;
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
      self.showFrame(track, Math.round(track.scrollLeft / track.clientWidth));
    };

    track.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      /* Nothing to drag: on desktop the layer shows one render and takes the
         strip's own scrolling away. */
      if (track.scrollWidth <= track.clientWidth) return;
      pointer = event.pointerId;
      startX = event.clientX;
      startLeft = track.scrollLeft;
      dragging = false;
    });

    track.addEventListener('pointermove', function (event) {
      if (event.pointerId !== pointer) return;

      var moved = event.clientX - startX;
      if (!dragging) {
        if (Math.abs(moved) < 6) return;
        dragging = true;
        track.classList.add('is-dragging');
        if (track.setPointerCapture) track.setPointerCapture(pointer);
      }
      event.preventDefault();
      track.scrollLeft = startLeft - moved;
    });

    track.addEventListener('pointerup', stop);
    track.addEventListener('pointercancel', stop);

    /* A photo is draggable by default, and that native drag swallows the
       pointer events this listens for halfway through the gesture. */
    track.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });
  };

  /* --------------------------------------------------------------------------
     Events

     One delegated listener on the section: the sheet's control is markup the
     cards do not own, and the cards' own controls come and go with the theme
     editor, so binding per button would need rebinding on every reload.
     -------------------------------------------------------------------------- */

  Picker.prototype.bind = function () {
    var self = this;

    /* pointerover / focusin / touchstart all bubble (pointerenter does not),
       so one listener on the section covers every card — including ones the
       theme editor adds later. */
    var onWarm = function (event) {
      var open = event.target.closest('[data-pblp-open]');
      if (!open || !self.root.contains(open)) return;
      self.warmSheet(open.closest('[data-pblp-card]'));
    };
    this.root.addEventListener('pointerover', onWarm);
    this.root.addEventListener('focusin', onWarm);
    this.root.addEventListener('touchstart', onWarm, { passive: true });
    this.warmIdle();

    var onClick = function (event) {
      var target = event.target;

      /* The sheet gets its own copy of this listener (below), because the
         desktop layer moves it out of the section and under <body> — a click on
         its close button or its add button then never bubbles through the
         section. While the sheet is still inside the section the click reaches
         both listeners; the section's copy stands down for anything inside the
         sheet so nothing is handled twice. */
      if (event.currentTarget === self.root && self.sheet && self.sheet.contains(target)) return;

      var open = target.closest('[data-pblp-open]');
      if (open) {
        self.open(open.closest('[data-pblp-card]'));
        return;
      }

      if (target.closest('[data-pblp-close]') || target.closest('[data-pblp-backdrop]')) {
        self.close();
        return;
      }

      if (target.closest('[data-pblp-checkout]')) {
        self.addToCart();
        return;
      }

      var button = target.closest('[data-pblp-add], [data-pblp-increase], [data-pblp-decrease]');
      if (!button) return;

      var card = button.closest('[data-pblp-card]') || self.openCard;
      if (!card) return;

      var step = self.stepOf(card);
      if (!step) return;

      var id = card.getAttribute('data-pblp-variant');
      var current = step.counts[id] || 0;
      var before = step.total();
      var decrease = button.hasAttribute('data-pblp-decrease');
      step.set(id, decrease ? current - 1 : current + 1);

      /* Only a tap that put something in: `set` clamps to the cap, so a plus
         on a full basket lands here too and changes nothing. */
      if (!decrease && step.total() > before) self.trackAdd(step, card, button);
    };

    this.root.addEventListener('click', onClick);
    if (this.sheet) this.sheet.addEventListener('click', onClick);

    this.bindPager();

    this.onKeydown = function (event) {
      if (event.key === 'Escape') {
        self.close();
        return;
      }
      if (!self.sheet || self.sheet.hidden) return;

      /* The arrows do with a keyboard what a swipe does with a finger, as long
         as the sheet is the phone's modal panel. Not while the focus is on a
         photo strip that can scroll — there they belong to the photos. */
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        if (self.sheet.getAttribute('aria-modal') !== 'true') return;
        var here = document.activeElement;
        var strip = here && here.closest && here.closest('[data-pblp-track]');
        if (strip && strip.scrollWidth > strip.clientWidth) return;
        if (self.pagerCards().length < 2) return;
        event.preventDefault();
        self.pageBy(event.key === 'ArrowRight' ? 1 : -1);
        return;
      }

      if (event.key !== 'Tab') return;
      if (self.sheet.getAttribute('aria-modal') !== 'true') return;

      var items = self.sheetFocusables();
      if (!items.length) {
        event.preventDefault();
        return;
      }
      var first = items[0];
      var last = items[items.length - 1];
      var current = document.activeElement;
      var inside = self.sheet.contains(current);
      if (event.shiftKey && (!inside || current === first)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (!inside || current === last)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', this.onKeydown);

    /* A derived step watches the basket it derives from. Every step is on the
       same page, so a can picked in the paid one has to change what the free
       one allows, and what the bottles one recommends, there and then — and it
       announces every change already.

       A bottles step repaints on any change rather than only on its own
       source's: how many bottles it recommends is how many kinds of can are in
       the basket, and a free can is a kind of can too. A summary step reads
       every basket, so it repaints on all of them. */
    this.onChange = function (event) {
      if (!event.detail) return;
      /* The basket changed, so whatever went wrong on the last hand-off is
         about a bundle that no longer exists. */
      self.clearError();
      self.steps.forEach(function (step) {
        if (step.derived() && step.source === event.detail.bundleId) step.sync(true);
        else if (step.mode === 'bottles' || step.mode === 'summary') step.render();
      });
      /* A step that just appeared or disappeared renumbers the steps under it
         and changes what the seams say. Recomputing which step is in view as
         well would undo the
         tap that got here: acting in a step is what makes it the step in view,
         and the scroll position has not moved. Only a step that vanished has
         to hand the bar back. */
      if (self.active && self.active.root.hidden) self.refreshActive();
      else self.paintLabels();
    };
    document.addEventListener('pblp:change', this.onChange);

    /* Which step owns the bar follows the scroll, once per frame. */
    var queued = false;
    this.onScroll = function () {
      /* Nothing to place while the section is nowhere near the viewport:
         the FAQ at the foot of the page used to cost a label walk and a
         round of measurements per scroll frame. The observer below repaints
         once on the way in and on the way out. */
      if (!self.inView) return;
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        self.refreshActive();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    /* BACK FROM THE CHECKOUT. `addToCart` leaves for the checkout with the
       button disabled and `sending` set, and clears the saved baskets on the
       way out. A browser that brings the page back from its back-forward
       cache brings it back exactly like that: button grey, `sending` true,
       every tap on it ignored, and nothing saved behind the bundle that is
       still on the screen. `pageshow` with `persisted` is that moment. */
    this.onPageShow = function (event) {
      if (event.persisted) self.resume();
    };
    window.addEventListener('pageshow', this.onPageShow);

    if ('IntersectionObserver' in window) {
      this.viewWatcher = new IntersectionObserver(
        function (entries) {
          self.inView = entries[entries.length - 1].isIntersecting;
          self.refreshActive();
        },
        /* Generous, so the bar has changed hands before the edge is in sight. */
        { rootMargin: '50% 0px' }
      );
      this.viewWatcher.observe(this.root);
    }
  };

  /* --------------------------------------------------------------------------
     The hand-off

     THE ONE PLACE THIS PAGE LEAVES THE VIRTUAL BASKET. Everything above it is
     localStorage, because the shop runs cart sync and a half-built bundle must
     not leak into every other surface.

     IT DOES NOT GO INTO THE SHOPPER'S CART. These are pre-sale products that
     ship on their own schedule, so a pre-sale order may not carry anything
     else. The bundle therefore gets a cart of its own and the button goes
     straight to that cart's checkout — whatever the shopper already had stays
     untouched, and nothing of theirs can end up in this order. That is
     PBCart.createDetachedCheckout; every other PBCart method works on the one
     shared cart, so none of them can be used here.

     THE FREE ITEMS GO IN AT FULL PRICE. A line item's price is Shopify's to
     decide, not the theme's, so what makes a gift free is a discount on the
     shop's side: the liquids branch of the discount function in
     planet-b-online-discount. Without it the checkout charges for the cans and
     bottles this page called free.

     EVERY LINE CARRIES ITS PLACE IN THE BUNDLE, and nothing more. The kind and
     how many of its units are free are structural facts the checkout side
     cannot work out for itself once the lines are merged into one. Prices and
     totals are deliberately NOT stamped here: the cart transform sees the real
     cart prices and adds those up itself, so a crafted request cannot claim a
     bundle is worth more than it is. Claiming more free units than the tier
     allows earns nothing either — the ladder caps it, and inflating the count
     lowers the paid can count, which only drops the tier.
     -------------------------------------------------------------------------- */

  Picker.prototype.lines = function () {
    /* ONE LINE PER VARIANT, however many steps it came from. Shopify keeps two
       cart lines apart when any attribute differs, so a conditioner bought in
       step one and earned in step two used to show up twice in the checkout —
       once as 1× and once as 6×. Totalling here is what puts it on one line,
       and the count of free units rides along as a number instead of a flag so
       nothing is lost by doing it. */
    var totals = {};
    var order = [];

    this.steps.forEach(function (step) {
      if (step.mode === 'summary' || !step.onPage()) return;

      var free = step.mode === 'bottles' ? step.earned || 0 : 0;

      step.cards.forEach(function (card) {
        var id = card.getAttribute('data-pblp-variant');
        var quantity = step.counts[id] || 0;
        if (quantity <= 0) return;

        var earned;
        if (step.mode === 'free') earned = quantity;
        else if (step.mode === 'bottles') {
          earned = Math.min(quantity, free);
          free -= earned;
        } else earned = 0;

        if (!totals[id]) {
          /* Bottles are the only thing a bottles step sells; everything else on
             this page is a can. The kind travels with the line because the
             checkout side prices the two differently and cannot tell them apart
             once the lines are merged into one. A variant only ever appears as
             one kind, so the first step to carry it settles it. */
          totals[id] = {
            id: id,
            quantity: 0,
            free: 0,
            kind: step.mode === 'bottles' ? 'bottle' : 'can',
          };
          order.push(id);
        }

        totals[id].quantity += quantity;
        totals[id].free += earned;
      });
    });

    return order.map(function (id) {
      return totals[id];
    });
  };

  Picker.prototype.addToCart = function () {
    var self = this;
    var lines = this.lines();
    if (!lines.length || this.sending) return;

    /* The button is off under the order minimum; this is the same rule for a
       click that reaches here anyway (keyboard, a stale button). */
    if (this.shortOfMin() > 0) {
      this.gateCheckout();
      return;
    }

    if (!window.PBCart || !window.PBCart.createDetachedCheckout) {
      console.error('[pblp] PBCart.createDetachedCheckout is missing');
      this.trackCheckout(lines, '', 'NO_CART');
      this.showError(false);
      return;
    }

    this.sending = true;
    this.clearError();
    if (this.checkout) {
      this.checkout.disabled = true;
      this.checkout.classList.add('is-busy');
      this.checkout.setAttribute('aria-busy', 'true');
    }

    /* One id per hand-off, so the cart transform groups the lines of this
       bundle and only these. Two bundles in one cart would otherwise merge
       into a single line. */
    var bundleId = 'pblp-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
    var title = this.copy.bundleTitle || 'WONDR Liquids bundel';
    var code = this.copy.sumDiscountCode;

    window.PBCart
      .createDetachedCheckout({
        items: lines.map(function (line) {
          return {
            id: line.id,
            quantity: line.quantity,
            properties: {
              _pblp_bundle: 'true',
              _pblp_bundle_id: bundleId,
              _pblp_bundle_title: title,
              _pblp_kind: line.kind,
              /* How many of this line's units are free. A count rather than a
                 flag, so one line can hold both — which is what keeps the same
                 variant from appearing twice in the checkout. */
              _pblp_free: String(line.free),
            },
          };
        }),
        attributes: { _pb_presale: 'liquids' },
        discountCodes: code ? [code] : [],
      })
      .then(function (result) {
        /* The URL is in hand: this is the moment the metric can carry it.
           Before the baskets are cleared, because the properties read them.

           `directCheckoutUrl`, not `checkoutUrl`: the event's URL is for a
           Klaviyo flow, and what the Storefront API returns is a cart
           permalink whose `key` a click tracker can break and whose route
           drops these pre-sale lines in the shopper's own cart on the way
           past. See createDetachedCheckout in pb-cart.js. The navigation
           below keeps the cart permalink — it is happening now, in this
           browser, with nothing in between to mangle it. */
        self.trackCheckout(lines, result.directCheckoutUrl || result.checkoutUrl, null);

        /* The bundle is in a checkout now, so the baskets that held it have
           done their job — leaving them would rebuild it on the next visit on
           top of an order that is already placed. Cleared only once the
           checkout URL is in hand, so a failed hand-off loses nothing. */
        self.steps.forEach(function (step) {
          if (step.mode === 'summary') return;
          try {
            window.localStorage.removeItem(STORE_PREFIX + step.bundleId);
          } catch (error) {
            /* Blocked storage: nothing to clear. */
          }
        });

        window.location.href = result.checkoutUrl;
      })
      .catch(function (error) {
        console.error('[pblp] hand-off failed', error);
        self.sending = false;
        if (self.checkout) {
          self.checkout.disabled = self.shortOfMin() > 0;
          self.checkout.classList.remove('is-busy');
          self.checkout.removeAttribute('aria-busy');
        }
        /* Say so. A console line is not a message: the visitor saw the
           button go grey and come back, and nothing else. The basket is
           untouched, so the button is the retry. Stock and a refused cart
           are their own message — a retry will not fix those, the bundle
           will. `pb-cart.js` marks them (code STOCK / REFUSED); the rest —
           network, the 15s bound, a 5xx — is the general one. */
        var code = error && error.code;
        self.trackCheckout(lines, '', code);
        self.showError(code === 'STOCK' || code === 'REFUSED');
      });
  };

  /* The page is back on screen after a hand-off that left it (or that never
     got an answer): the button works again, and the baskets on the screen are
     saved again, so a reload shows what the visitor is looking at. A visitor
     who comes back from the checkout has most likely not paid — a placed order
     ends on the thank-you page, and Back from there does not return here. */
  Picker.prototype.resume = function () {
    this.sending = false;
    if (this.checkout) {
      this.checkout.disabled = this.shortOfMin() > 0;
      this.checkout.classList.remove('is-busy');
      this.checkout.removeAttribute('aria-busy');
    }
    this.clearError();
    this.steps.forEach(function (step) {
      if (step.mode !== 'summary' && step.onPage() && step.total() > 0) step.persist();
    });
  };

  Picker.prototype.showError = function (stock) {
    if (!this.error) return;
    var text = stock ? this.copy.checkoutStock : this.copy.checkoutError;
    if (!text) {
      text = stock
        ? 'Een van je producten is niet meer op voorraad. Pas je bundel aan en probeer het opnieuw.'
        : 'Afrekenen is niet gelukt. Controleer je verbinding en probeer het opnieuw.';
    }
    this.error.textContent = text;
    this.error.hidden = false;
  };

  Picker.prototype.clearError = function () {
    if (!this.error || this.error.hidden) return;
    this.error.textContent = '';
    this.error.hidden = true;
  };

  Picker.prototype.destroy = function () {
    this.close();
    /* The close above armed a fade-out; the section is going away now, so it
       gets neither its timer nor its listener. */
    this.settleClose();
    if (this.sheet) this.sheet.hidden = true;
    if (this.backdrop) this.backdrop.hidden = true;
    this.releasePage();
    if (this.viewWatcher) this.viewWatcher.disconnect();
    window.clearTimeout(this.badgeTimer);
    this.steps.forEach(function (step) {
      window.clearTimeout(step.flashTimer);
    });
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('pblp:change', this.onChange);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    window.removeEventListener('pageshow', this.onPageShow);
  };

  /* --------------------------------------------------------------------------
     Lifecycle

     The theme editor tears a section down and rebuilds it on every setting
     change, so the controller is attached per section element and re-attached
     on `shopify:section:load`.
     -------------------------------------------------------------------------- */

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-pblp-section]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.pbLiquidPicker) return;
      root.pbLiquidPicker = new Picker(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector('[data-pblp-section]');
    if (root && root.pbLiquidPicker) {
      root.pbLiquidPicker.destroy();
      root.pbLiquidPicker = null;
    }
  });
})();
